<template>
  <CurrentEventDataProvider v-slot="{ hasFullEventDetails, locale }">
    <div
      class="live-stream-page"
      style="
        background-color: var(--background-color, #f3f4f6);
        min-height: 100vh;
        font-family: 'Poppins', sans-serif;
      "
    >
      <Navbar :show-nav="true" />

      <div
        v-if="hasFullEventDetails"
        class="ls-wrap"
        id="main-content"
        role="main"
      >
        <BookSessionsDataProvider v-slot="{}">
          <AgendasDataProvider
            v-slot="{ trackById, tracks, setStreamFromSession }"
          >
            <StreamDataProvider
              :set-from-current-event="!sessionIdFromQuery"
              v-slot="{
                isReady,
                isImage,
                hasStream,
                embedUrl,
                embedType,
                iframeAllowAttributes
              }"
            >
              <DocumentsDataProvider
                v-slot="{ documents }"
                :applicablePlatforms="['digital', 'webApp', 'sponsor', 'stage']"
              >
                <div
                  class="ls-grid"
                  :class="{
                    'ls-grid--rail-only': !activeTab && !hasPromotedMessage,
                    'ls-grid--embedded': !!$slots.main
                  }"
                >
                  <!-- Mobile-only slot rendered above everything — used by
                       Program.vue to lift its greeting + My Programs pills
                       out of the hero banner and place them above the
                       promoted message on small screens. Hidden on
                       desktop (where the pills stay over the banner). -->
                  <div v-if="$slots['mobile-top']" class="ls-mobile-top">
                    <slot name="mobile-top" />
                  </div>

                  <!-- ── LEFT: header + video player ─────────────── -->
                  <div
                    ref="leftColumn"
                    class="ls-left"
                    :class="{
                      'ls-left--embedded': !!$slots.main,
                      'ls-left--has-about':
                        !$slots.main && !!sessionDescriptionHtml
                    }"
                  >
                    <!--
                      `main` slot — when a parent page (Program.vue)
                      uses LiveStream as its shell to share the right
                      side panel, that page populates this slot with
                      its own central content (hero / filters /
                      sessions). The default content below — the
                      stream header, video player and about block —
                      only renders when no slot content was supplied,
                      i.e. on the live-stream route itself.
                    -->
                    <slot name="main">
                      <!-- Header row -->
                      <div class="ls-header">
                        <button
                          class="ls-header__back"
                          @click="goBack"
                          :aria-label="$t('Views.LiveStream.back')"
                        >
                          <i class="fas fa-arrow-left"></i>
                        </button>
                        <div class="ls-header__title-block">
                          <h1 class="ls-header__title">
                            {{
                              currentSession.title ||
                              $t('Views.LiveStream.liveStream')
                            }}
                          </h1>
                          <div
                            v-if="
                              speakerNames(currentSession) || sessionTimeLabel
                            "
                            class="ls-header__meta"
                          >
                            <span
                              v-if="speakerNames(currentSession)"
                              class="ls-header__speaker"
                              >{{ speakerNames(currentSession) }}</span
                            >
                            <span
                              v-if="
                                speakerNames(currentSession) && sessionTimeLabel
                              "
                              class="ls-header__sep"
                              aria-hidden="true"
                              >•</span
                            >
                            <span
                              v-if="sessionTimeLabel"
                              class="ls-header__time"
                            >
                              <i class="far fa-clock" aria-hidden="true"></i>
                              {{ sessionTimeLabel }}
                            </span>
                          </div>
                        </div>
                        <!-- Live badge — desktop only. On mobile this
                           same slot is taken by the Show/Hide stream
                           toggle below. -->
                        <div
                          v-if="isLiveSession(currentSession)"
                          class="ls-live-badge ls-live-badge--desktop"
                        >
                          <span class="ls-live-badge__dot"></span>
                          {{ $t('Views.LiveStream.live') }}
                        </div>
                        <!-- Show / Hide stream toggle — mobile only,
                           sits in the same header slot as the Live
                           badge above so the layout stays consistent. -->
                        <button
                          type="button"
                          class="ls-stream-toggle ls-stream-toggle--mobile"
                          @click="streamHidden = !streamHidden"
                          :aria-pressed="streamHidden ? 'true' : 'false'"
                        >
                          <i
                            :class="
                              streamHidden ? 'far fa-eye' : 'far fa-eye-slash'
                            "
                            aria-hidden="true"
                          ></i>
                          <span>{{
                            streamHidden
                              ? $t('Views.LiveStream.showStream')
                              : $t('Views.LiveStream.hideStream')
                          }}</span>
                        </button>
                      </div>

                      <!-- Video player area — overlay chrome (watching
                         count, active count, volume/settings/fullscreen
                         controls) removed so the stream shows clean. -->
                      <div v-if="!streamHidden" class="ls-player">
                        <div class="ls-player__video">
                          <StageStream
                            v-if="hasStream && isReady && embedUrl"
                            :isReady="isReady"
                            :isImage="isImage"
                            :hasStream="hasStream"
                            :embedUrl="embedUrl"
                            :embedType="embedType"
                            :iframeAllowAttributes="iframeAllowAttributes"
                            class="ls-player__stream"
                          />
                          <div v-else class="ls-player__placeholder">
                            <div class="ls-player__eye">
                              <i class="far fa-eye"></i>
                            </div>
                            <div class="ls-player__active-text">
                              {{
                                embedUrl
                                  ? $t('Views.LiveStream.loadingStream')
                                  : $t('Views.LiveStream.noLiveStream')
                              }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- About this session — pulled from the agenda
                         entry's description (HTML), rendered the same
                         way the Sponsor live-stream page renders its
                         "About" block. Hides itself when the admin
                         hasn't supplied any description text, AND
                         on mobile when the stream itself has been
                         collapsed via the Hide-stream toggle. -->
                      <div
                        v-if="sessionDescriptionHtml"
                        class="ls-about"
                        :class="{ 'ls-about--mobile-hidden': streamHidden }"
                      >
                        <h2 class="ls-about__title">
                          {{
                            currentSession.title
                              ? $t('Views.LiveStream.aboutSession', {
                                  title: currentSession.title
                                })
                              : $t('Views.LiveStream.aboutThisSession')
                          }}
                        </h2>
                        <div
                          class="ls-about__body html-content"
                          v-html="sessionDescriptionHtml"
                        />
                      </div>
                    </slot>
                  </div>

                  <!-- ── RIGHT side: master menu / detail panel ── -->
                  <div
                    class="ls-side"
                    :class="{
                      'ls-side--detail': activeTab,
                      'ls-side--with-promoted':
                        hasPromotedMessage || activePromotedPoll,
                      'ls-side--rail-only':
                        !activeTab &&
                        !hasPromotedMessage &&
                        !activePromotedPoll,
                      'ls-side--drawer-open': sidebarOpen
                    }"
                    ref="sidePane"
                  >
                    <!-- Main column — either the promoted message
                         (default when admin promoted something), the
                         master menu, or the active section's panel. -->
                    <div class="ls-side__main">
                      <!-- PROMOTED MESSAGE — top of the side pane when
                           the admin has promoted a poll / post. Visible
                           only while the user hasn't drilled into a
                           specific section. -->
                      <div
                        v-if="
                          (hasPromotedMessage || activePromotedPoll) &&
                          !activeTab
                        "
                        class="ls-promoted"
                      >
                        <!-- Poll-active header — matches the visual
                             pattern of the other section panels (Participants,
                             Speakers, etc.) with an icon-only back button,
                             a title and a small subtitle. -->
                        <div
                          v-if="activePromotedPoll"
                          class="ls-promoted__panel-header"
                        >
                          <button
                            type="button"
                            class="ls-promoted__back"
                            @click="dismissPromotedPoll"
                            :aria-label="$t('Views.LiveStream.backToPromoted')"
                          >
                            <i class="fas fa-arrow-left" aria-hidden="true"></i>
                          </button>
                          <div class="ls-promoted__panel-titles">
                            <h3 class="ls-promoted__panel-title">
                              {{ $t('Views.LiveStream.livePoll') }}
                            </h3>
                            <p class="ls-promoted__panel-sub">
                              {{ $t('Views.LiveStream.castYourVote') }}
                            </p>
                          </div>
                        </div>
                        <!-- Default promoted-message header — pill stays
                             visible only while no poll is active. -->
                        <div v-else class="ls-promoted__header">
                          <span class="ls-promoted__pill">
                            <i class="fal fa-bullhorn" aria-hidden="true"></i>
                          </span>
                        </div>
                        <div class="ls-promoted__body">
                          <!-- Already-answered short-circuit — if we
                               know the user has submitted this poll
                               before, show a friendly thanks view
                               instead of remounting the form. -->
                          <div
                            v-if="
                              activePromotedPoll &&
                              alreadyAnsweredPollId ===
                                String(activePromotedPoll.id)
                            "
                            class="ls-promoted__poll ls-promoted__poll--answered"
                          >
                            <div class="ls-promoted__answered">
                              <div class="ls-promoted__answered-icon">
                                <i class="fas fa-check" aria-hidden="true"></i>
                              </div>
                              <h4 class="ls-promoted__answered-title">
                                {{ $t('Views.LiveStream.alreadyAnswered') }}
                              </h4>
                              <p class="ls-promoted__answered-copy">
                                {{ $t('Views.LiveStream.alreadyAnsweredCopy') }}
                              </p>
                              <button
                                type="button"
                                class="ls-promoted__answered-cta"
                                @click="dismissPromotedPoll"
                              >
                                {{ $t('Views.LiveStream.backToMessage') }}
                              </button>
                            </div>
                          </div>
                          <!-- Inline poll rendering — when an attendee
                               clicks a poll button inside the promoted
                               message, we swap in the live Poll component
                               so the poll is votable right here in the
                               promoted area instead of opening a tab. -->
                          <div
                            v-else-if="activePromotedPoll"
                            class="ls-promoted__poll"
                          >
                            <Poll
                              :id="activePromotedPoll.id"
                              container="Timeline"
                            />
                          </div>
                          <template v-else>
                            <TimelinePromotedItem
                              v-for="item in rawPromotedItems"
                              :key="`promoted-${item.TimelineID}`"
                              :item="item"
                              :on-webapp="isOnWebapp"
                            />
                          </template>
                        </div>
                      </div>

                      <!-- (Detail back button is now inlined inside each
                           section's own header next to the title — see
                           `.ls-section-back` below.) -->

                      <!-- ── CHAT tab ─────────────────────────────── -->
                      <TimelineDataProvider
                        v-if="activeTab === 'chat'"
                        id="ls-panel-chat"
                        role="tabpanel"
                        aria-labelledby="ls-tab-chat"
                        :applicablePlatforms="[
                          'digital',
                          'webApp',
                          'sponsor',
                          'stage'
                        ]"
                        v-slot="{ timelineItems }"
                      >
                        <div class="ls-panel ls-panel--chat">
                          <div class="ls-chat-header ls-section-header">
                            <div class="ls-section-header__row">
                              <button
                                type="button"
                                class="ls-section-back"
                                @click="activeTab = null"
                                :aria-label="
                                  $t('Views.LiveStream.backToAllSections')
                                "
                              >
                                <i
                                  class="fas fa-arrow-left"
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <div class="ls-section-header__titles">
                                <h3 class="ls-chat-header__title">
                                  {{ $t('Views.LiveStream.posts') }}
                                </h3>
                                <p class="ls-chat-header__sub">
                                  {{
                                    $tc(
                                      'Views.LiveStream.messageCount',
                                      chatMessages(timelineItems).length,
                                      { n: chatMessages(timelineItems).length }
                                    )
                                  }}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            class="ls-chat-list"
                            ref="chatList"
                            aria-live="polite"
                            aria-relevant="additions"
                            role="log"
                          >
                            <div
                              v-for="item in chatMessages(timelineItems)"
                              :key="item.TimelineID"
                              class="ls-chat-msg"
                            >
                              <Avatar
                                :image="msgAuthor(item).Picture"
                                :name="msgAuthorName(item)"
                                :alt="msgAuthorName(item)"
                                :circle="true"
                                class="ls-chat-msg__avatar"
                              />
                              <div class="ls-chat-msg__body">
                                <div class="ls-chat-msg__head">
                                  <span class="ls-chat-msg__name">
                                    {{ msgAuthorName(item) }}
                                  </span>
                                  <span class="ls-chat-msg__time">{{
                                    formatChatTime(item.CreatedOn || item.Tid)
                                  }}</span>
                                </div>
                                <div
                                  v-if="
                                    item.replyTo ||
                                    (item.Author && item.replyToMessage)
                                  "
                                  class="ls-chat-msg__reply"
                                >
                                  <span class="ls-chat-msg__reply-name">{{
                                    item.replyTo || $t('Views.LiveStream.reply')
                                  }}</span>
                                  <span class="ls-chat-msg__reply-text">{{
                                    item.replyToMessage
                                  }}</span>
                                </div>
                                <div
                                  class="ls-chat-msg__text"
                                  v-html="cleanMessage(item.Message)"
                                />
                                <div class="ls-chat-msg__actions">
                                  <button
                                    type="button"
                                    class="ls-chat-msg__action"
                                    :class="{
                                      'ls-chat-msg__action--active':
                                        isPostLiked(item)
                                    }"
                                    :disabled="isLikingPost(item)"
                                    @click="togglePostLike(item)"
                                  >
                                    <i class="fas fa-thumbs-up"></i>
                                    {{ $t('Views.LiveStream.like')
                                    }}<span v-if="postLikeCount(item)">
                                      ({{ postLikeCount(item) }})</span
                                    >
                                  </button>
                                  <button
                                    v-if="isTimelineCommentingEnabled"
                                    type="button"
                                    class="ls-chat-msg__action"
                                    :class="{
                                      'ls-chat-msg__action--active':
                                        isCommentsOpen(item)
                                    }"
                                    @click="toggleComments(item)"
                                  >
                                    <i class="far fa-comment"></i>
                                    {{ $t('Views.LiveStream.comment')
                                    }}<span v-if="postCommentCount(item)">
                                      ({{ postCommentCount(item) }})</span
                                    >
                                  </button>
                                  <!-- Overflow menu (Reply / Message
                                       privately / Delete) — the ⋯ button
                                       reveals on message hover. -->
                                  <div class="ls-chat-msg__more">
                                    <button
                                      type="button"
                                      class="ls-chat-msg__action ls-chat-msg__more-btn"
                                      :class="{
                                        'ls-chat-msg__more-btn--open':
                                          isActionMenuOpen(item)
                                      }"
                                      :aria-label="
                                        $t('Views.LiveStream.moreActions')
                                      "
                                      :aria-expanded="
                                        isActionMenuOpen(item)
                                          ? 'true'
                                          : 'false'
                                      "
                                      @click.stop="toggleActionMenu(item)"
                                    >
                                      <i class="fas fa-ellipsis-h"></i>
                                    </button>
                                    <div
                                      v-if="isActionMenuOpen(item)"
                                      class="ls-chat-msg__menu"
                                      @click.stop
                                    >
                                      <button
                                        type="button"
                                        class="ls-chat-msg__menu-item"
                                        @click="
                                          setReplyTarget(item);
                                          closeActionMenu();
                                        "
                                      >
                                        <i class="fas fa-reply"></i>
                                        {{ $t('Views.LiveStream.reply') }}
                                      </button>
                                      <button
                                        v-if="
                                          isPrivateChatEnabled &&
                                          !isOwnMessage(item)
                                        "
                                        type="button"
                                        class="ls-chat-msg__menu-item"
                                        @click="
                                          startPrivateChat(item);
                                          closeActionMenu();
                                        "
                                      >
                                        <i class="fas fa-user"></i>
                                        {{
                                          $t(
                                            'Views.LiveStream.messagePrivately'
                                          )
                                        }}
                                      </button>
                                      <button
                                        v-if="canDeletePost(item)"
                                        type="button"
                                        class="ls-chat-msg__menu-item ls-chat-msg__menu-item--danger"
                                        :disabled="isDeletingPost(item)"
                                        @click="
                                          deletePost(item);
                                          closeActionMenu();
                                        "
                                      >
                                        <i class="far fa-trash-alt"></i>
                                        {{ $t('Views.LiveStream.delete') }}
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <!-- Comments thread -->
                                <div
                                  v-if="
                                    isCommentsOpen(item) &&
                                    isTimelineCommentingEnabled
                                  "
                                  class="ls-chat-comments"
                                >
                                  <div
                                    v-for="c in postComments(item)"
                                    :key="c.TimelineID"
                                    class="ls-chat-comment"
                                  >
                                    <Avatar
                                      :image="msgAuthor(c).Picture"
                                      :name="msgAuthorName(c)"
                                      :alt="msgAuthorName(c)"
                                      :circle="true"
                                      class="ls-chat-comment__avatar"
                                    />
                                    <div class="ls-chat-comment__bubble">
                                      <span class="ls-chat-comment__name">{{
                                        msgAuthorName(c)
                                      }}</span>
                                      <div
                                        class="ls-chat-comment__text"
                                        v-html="cleanMessage(c.Message)"
                                      />
                                    </div>
                                    <button
                                      v-if="canDeleteComment(c)"
                                      type="button"
                                      class="ls-chat-comment__delete"
                                      :aria-label="
                                        $t('Views.LiveStream.deleteComment')
                                      "
                                      @click="deleteCommentItem(item, c)"
                                    >
                                      <i class="fas fa-times"></i>
                                    </button>
                                  </div>
                                  <div
                                    v-if="isTimelineCommentingEnabled"
                                    class="ls-chat-comment-input"
                                  >
                                    <input
                                      :value="commentDrafts[item.TimelineID]"
                                      type="text"
                                      class="ls-chat-comment-input__field"
                                      :placeholder="
                                        $t('Views.LiveStream.writeComment')
                                      "
                                      :disabled="isCommentingPost(item)"
                                      @input="
                                        setCommentDraft(
                                          item,
                                          $event.target.value
                                        )
                                      "
                                      @keydown.enter.prevent="
                                        submitComment(item)
                                      "
                                    />
                                    <button
                                      type="button"
                                      class="ls-chat-comment-input__send"
                                      :disabled="
                                        isCommentingPost(item) ||
                                        !(
                                          commentDrafts[item.TimelineID] || ''
                                        ).trim()
                                      "
                                      @click="submitComment(item)"
                                    >
                                      <i
                                        v-if="isCommentingPost(item)"
                                        class="fas fa-circle-notch fa-spin"
                                      ></i>
                                      <span v-else>{{
                                        $t('Views.LiveStream.post')
                                      }}</span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!chatMessages(timelineItems).length"
                              class="ls-chat-empty"
                            >
                              <i
                                class="far fa-comments"
                                style="
                                  font-size: 28px;
                                  color: #d1d5db;
                                  margin-bottom: 8px;
                                "
                              ></i>
                              <p>
                                {{ $t('Views.LiveStream.noMessages') }}
                              </p>
                            </div>
                          </div>

                          <!-- Reply preview banner -->
                          <div
                            v-if="replyTarget && isTimelinePostingEnabled"
                            class="ls-chat-reply-banner"
                            aria-live="polite"
                            role="status"
                          >
                            <div class="ls-chat-reply-banner__body">
                              <i
                                class="fas fa-reply"
                                style="color: var(--color-primary, #047873)"
                              ></i>
                              <div>
                                <div class="ls-chat-reply-banner__name">
                                  {{
                                    $t('Views.LiveStream.replyingTo', {
                                      name: msgAuthorName(replyTarget)
                                    })
                                  }}
                                </div>
                                <div class="ls-chat-reply-banner__text">
                                  {{ stripHtml(replyTarget.Message) }}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              class="ls-chat-reply-banner__close"
                              @click="replyTarget = null"
                              :aria-label="$t('Views.LiveStream.cancelReply')"
                            >
                              <i class="fas fa-times"></i>
                            </button>
                          </div>

                          <div
                            v-if="pendingAttachment && isTimelinePostingEnabled"
                            class="ls-chat-attach-preview"
                          >
                            <div
                              class="ls-chat-attach-preview__icon"
                              :class="`ls-chat-attach-preview__icon--${pendingAttachment.kind}`"
                            >
                              <img
                                v-if="pendingAttachment.kind === 'image'"
                                :src="pendingAttachment.dataUrl"
                                :alt="pendingAttachment.name"
                              />
                              <i
                                v-else-if="pendingAttachment.kind === 'video'"
                                class="fas fa-film"
                              ></i>
                              <i v-else class="fas fa-paperclip"></i>
                            </div>
                            <div class="ls-chat-attach-preview__info">
                              <div class="ls-chat-attach-preview__name">
                                {{ pendingAttachment.name }}
                              </div>
                              <div class="ls-chat-attach-preview__size">
                                {{ formatFileSize(pendingAttachment.size) }}
                              </div>
                            </div>
                            <button
                              type="button"
                              class="ls-chat-attach-preview__remove"
                              @click="pendingAttachment = null"
                              :aria-label="
                                $t('Views.LiveStream.removeAttachment')
                              "
                            >
                              <i class="fas fa-times"></i>
                            </button>
                          </div>

                          <form
                            v-if="isTimelinePostingEnabled"
                            class="ls-chat-input"
                            @submit.prevent="sendChatMessage"
                          >
                            <input
                              ref="chatFileInput"
                              type="file"
                              class="ls-chat-input__file"
                              accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip"
                              @change="onChatFileSelected"
                            />
                            <!-- 1. Attach button -->
                            <button
                              type="button"
                              class="ls-chat-input__attach"
                              @click="triggerChatFilePicker"
                              :aria-label="$t('Views.LiveStream.attachFile')"
                            >
                              <i class="fas fa-paperclip"></i>
                            </button>
                            <!-- 2. Type box with emoji button inside it -->
                            <div class="ls-chat-input__field-wrap">
                              <input
                                v-model="chatInput"
                                type="text"
                                :placeholder="$t('Views.LiveStream.typePost')"
                                class="ls-chat-input__field"
                                :aria-label="$t('Views.LiveStream.postMessage')"
                                @keydown.escape="emojiPickerOpen = false"
                              />
                              <button
                                type="button"
                                class="ls-chat-input__emoji"
                                :aria-label="$t('Views.LiveStream.insertEmoji')"
                                :aria-expanded="
                                  emojiPickerOpen ? 'true' : 'false'
                                "
                                @click.stop="toggleEmojiPicker"
                              >
                                <i class="far fa-smile"></i>
                              </button>
                              <transition name="ls-emoji-fade">
                                <div
                                  v-if="emojiPickerOpen"
                                  class="ls-emoji-picker"
                                  role="dialog"
                                  :aria-label="
                                    $t('Views.LiveStream.emojiPicker')
                                  "
                                  @click.stop
                                >
                                  <button
                                    v-for="emoji in emojiPalette"
                                    :key="emoji"
                                    type="button"
                                    class="ls-emoji-picker__btn"
                                    @click="insertEmoji(emoji)"
                                  >
                                    {{ emoji }}
                                  </button>
                                </div>
                              </transition>
                            </div>
                            <!-- 3. Send button -->
                            <button
                              type="submit"
                              class="ls-chat-input__send"
                              :disabled="
                                (!chatInput.trim() && !pendingAttachment) ||
                                sendingChat
                              "
                              :aria-label="$t('Views.Stage.Timeline.send')"
                            >
                              <i class="far fa-paper-plane"></i>
                            </button>
                          </form>
                        </div>
                      </TimelineDataProvider>

                      <!-- ── DOCS tab ─────────────────────────────── -->
                      <div
                        v-else-if="activeTab === 'docs'"
                        class="ls-panel"
                        id="ls-panel-docs"
                        role="tabpanel"
                        aria-labelledby="ls-tab-docs"
                      >
                        <div class="ls-docs-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-docs-header__title">
                                {{ $t('Views.LiveStream.sessionDocuments') }}
                              </h3>
                              <p class="ls-docs-header__sub">
                                {{
                                  $tc(
                                    'Views.LiveStream.filesAvailable',
                                    docsToShow(documents).length,
                                    { n: docsToShow(documents).length }
                                  )
                                }}
                              </p>
                            </div>
                            <button
                              v-if="docsToShow(documents).length"
                              type="button"
                              class="ls-docs-mailall"
                              :class="{
                                'ls-docs-mailall--sending': isEmailingAllDocs
                              }"
                              :disabled="isEmailingAllDocs"
                              @click="emailAllDocuments"
                              :title="
                                isEmailingAllDocs
                                  ? $t('Views.LiveStream.sending')
                                  : $t('Views.LiveStream.emailAllDocsToMe')
                              "
                            >
                              <i
                                v-if="isEmailingAllDocs"
                                class="fas fa-circle-notch fa-spin"
                              ></i>
                              <i v-else class="far fa-envelope"></i>
                              <span>{{ $t('Views.LiveStream.emailAll') }}</span>
                            </button>
                          </div>
                        </div>
                        <div class="ls-docs-list">
                          <div
                            v-for="doc in docsToShow(documents)"
                            :key="doc.DocumentID"
                            class="ls-doc-card"
                          >
                            <div class="ls-doc-card__row">
                              <div
                                class="ls-doc-card__icon"
                                :class="docIconClass(doc)"
                              >
                                <i :class="docIcon(doc)"></i>
                              </div>
                              <div class="ls-doc-card__info">
                                <div class="ls-doc-card__name-row">
                                  <span
                                    class="ls-doc-card__name"
                                    :title="doc.Name"
                                    >{{ doc.Name }}</span
                                  >
                                </div>
                                <div class="ls-doc-card__meta">
                                  <span class="ls-doc-card__type">{{
                                    docExt(doc)
                                  }}</span>
                                  <span v-if="doc.Size" class="ls-doc-card__sep"
                                    >•</span
                                  >
                                  <span v-if="doc.Size">{{
                                    formatBytes(doc.Size)
                                  }}</span>
                                </div>
                                <div class="ls-doc-card__date">
                                  {{
                                    $t('Views.LiveStream.uploadedOn', {
                                      date: formatDocDate(doc.uTime)
                                    })
                                  }}
                                </div>
                              </div>
                            </div>
                            <div class="ls-doc-card__actions">
                              <button
                                type="button"
                                class="ls-doc-btn ls-doc-btn--secondary"
                                :disabled="!docUrl(doc)"
                                @click="previewDocument(doc)"
                              >
                                <i class="far fa-eye"></i>
                                {{ $t('Views.LiveStream.preview') }}
                              </button>
                              <button
                                type="button"
                                class="ls-doc-btn ls-doc-btn--primary"
                                :disabled="!docUrl(doc)"
                                @click="downloadDocument(doc)"
                              >
                                <i class="fas fa-download"></i>
                                {{ $t('Views.Stage.Documents.download') }}
                              </button>
                            </div>
                          </div>
                          <div
                            v-if="!docsToShow(documents).length"
                            class="ls-empty"
                          >
                            <i
                              class="far fa-folder-open"
                              style="
                                font-size: 28px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.LiveStream.noDocuments') }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- ── PEOPLE tab ───────────────────────────── -->
                      <div
                        v-else-if="activeTab === 'people'"
                        class="ls-panel"
                        id="ls-panel-people"
                        role="tabpanel"
                        aria-labelledby="ls-tab-people"
                      >
                        <div class="ls-people-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-people-header__title">
                                {{
                                  $t('Views.Stage.Participants.participants')
                                }}
                              </h3>
                              <p class="ls-people-header__sub">
                                {{ peopleCountLabel }}
                              </p>
                            </div>
                          </div>
                          <div class="ls-people-search">
                            <i class="fas fa-search ls-people-search__icon"></i>
                            <input
                              v-model="peopleSearch"
                              type="text"
                              :placeholder="
                                $t('Views.LiveStream.searchParticipants')
                              "
                              class="ls-people-search__input"
                              :aria-label="
                                $t(
                                  'Views.Stage.Participants.searchForParticipants'
                                )
                              "
                            />
                          </div>
                        </div>
                        <ul
                          ref="peopleList"
                          class="ls-people-list"
                          role="list"
                          :aria-label="
                            $t('Views.Stage.Participants.participants')
                          "
                          @scroll.passive="onPeopleScroll"
                        >
                          <li
                            v-for="user in filteredParticipants"
                            :key="user.UserID"
                            class="ls-person-card"
                            :class="{
                              'ls-person-card--expanded':
                                expandedPersonId === user.UserID
                            }"
                          >
                            <div class="ls-person-card__row">
                              <div class="ls-person-card__avatar-wrap">
                                <Avatar
                                  :image="user.Picture"
                                  :name="
                                    (user.Firstname || '') +
                                    ' ' +
                                    (user.Lastname || '')
                                  "
                                  :alt="
                                    (user.Firstname || '') +
                                    ' ' +
                                    (user.Lastname || '')
                                  "
                                  :circle="true"
                                  class="ls-person-card__avatar"
                                />
                                <span
                                  v-if="isPersonOnline(user.UserID)"
                                  class="ls-person-card__status-dot"
                                  :aria-label="$t('Views.LiveStream.online')"
                                ></span>
                              </div>
                              <div class="ls-person-card__info">
                                <div class="ls-person-card__name">
                                  {{ user.Firstname }} {{ user.Lastname }}
                                </div>
                                <div
                                  v-if="user.Jobtitle || user.Company"
                                  class="ls-person-card__role"
                                >
                                  <template v-if="user.Jobtitle">{{
                                    user.Jobtitle
                                  }}</template>
                                  <template
                                    v-if="user.Jobtitle && user.Company"
                                  >
                                    ·
                                  </template>
                                  <template v-if="user.Company">{{
                                    user.Company
                                  }}</template>
                                </div>
                              </div>

                              <!-- Right actions: DM, then "View details". -->
                              <div class="ls-person-card__right">
                                <button
                                  v-if="
                                    isPrivateChatEnabled &&
                                    user.UserID !== (profile && profile.UserID)
                                  "
                                  class="ls-person-card__msg-btn"
                                  @click="openPrivateChatWith(user)"
                                  :aria-label="
                                    $t('Views.LiveStream.sendPrivateMessage')
                                  "
                                >
                                  <i class="far fa-comment"></i>
                                </button>
                                <button
                                  v-if="participantHasContactDetails(user)"
                                  class="ls-moreinfo"
                                  :class="{
                                    'ls-moreinfo--open':
                                      expandedPersonId === user.UserID
                                  }"
                                  :aria-expanded="
                                    expandedPersonId === user.UserID
                                      ? 'true'
                                      : 'false'
                                  "
                                  @click="togglePersonDetails(user)"
                                >
                                  <span>{{
                                    expandedPersonId === user.UserID
                                      ? $t('Views.LiveStream.hideDetails')
                                      : $t('Views.LiveStream.viewDetails')
                                  }}</span>
                                  <i
                                    class="fas fa-chevron-down ls-moreinfo__chevron"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </div>

                            <!-- Expanded contact details -->
                            <div
                              v-if="expandedPersonId === user.UserID"
                              class="ls-person-card__expanded"
                            >
                              <a
                                v-if="participantEmailVisible(user)"
                                class="ls-detail"
                                :href="'mailto:' + participantEmail(user)"
                                :title="participantEmail(user)"
                                @click.stop
                              >
                                <span
                                  class="ls-detail__icon ls-detail__icon--teal"
                                >
                                  <i class="far fa-envelope"></i>
                                </span>
                                <span class="ls-detail__text">{{
                                  participantEmail(user)
                                }}</span>
                              </a>
                              <a
                                v-if="participantPhoneVisible(user)"
                                class="ls-detail"
                                :href="'tel:' + participantPhone(user)"
                                @click.stop
                              >
                                <span
                                  class="ls-detail__icon ls-detail__icon--green"
                                >
                                  <i class="fas fa-phone"></i>
                                </span>
                                <span class="ls-detail__text">{{
                                  participantPhone(user)
                                }}</span>
                              </a>
                            </div>
                          </li>
                          <li
                            v-if="isPeopleLoading"
                            class="ls-empty ls-people-loading"
                          >
                            <i
                              class="fas fa-spinner fa-spin"
                              aria-hidden="true"
                            ></i>
                            <span class="sr-only">{{
                              $t('Views.LiveStream.loadingParticipants')
                            }}</span>
                          </li>
                          <li
                            v-else-if="!filteredParticipants.length"
                            class="ls-empty"
                          >
                            <i
                              class="far fa-user"
                              style="
                                font-size: 28px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>
                              {{
                                peopleSearch
                                  ? $t('Views.LiveStream.noParticipantsMatch')
                                  : $t('Views.LiveStream.noParticipantsLoaded')
                              }}
                            </p>
                          </li>
                        </ul>
                      </div>

                      <!-- ── EVENT INFO tab ───────────────────────── -->
                      <div
                        v-else-if="activeTab === 'eventInfo'"
                        class="ls-panel ls-panel--scroll"
                        id="ls-panel-eventInfo"
                        role="tabpanel"
                        aria-labelledby="ls-tab-eventInfo"
                      >
                        <div class="ls-info-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-info-header__title">
                                {{ $t('Views.LiveStream.eventInfo') }}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div class="ls-info-body">
                          <h4 class="ls-info__name" v-if="eventName">
                            {{ eventName }}
                          </h4>
                          <p class="ls-info__when" v-if="eventDateLabel">
                            <i class="far fa-calendar-alt"></i>
                            {{ eventDateLabel }}
                          </p>
                          <p class="ls-info__where" v-if="eventLocation">
                            <i class="fas fa-map-marker-alt"></i>
                            {{ eventLocation }}
                          </p>
                          <div
                            v-if="eventAbout"
                            class="ls-info__about html-content"
                            v-html="eventAbout"
                          />

                          <!-- FAQ / lobby info pages (e.g. "Future enhancements")
                             — pulled from the same Pages store the lobby
                             FaqSection uses, rendered as collapsible cards. -->
                          <div v-if="eventPages.length" class="ls-info__faqs">
                            <div
                              v-for="(page, i) in eventPages"
                              :key="page.PageID || page.Id || page.id || i"
                              :data-id="page.PageID || page.Id || page.id"
                              class="ls-faq-item"
                              :class="{ 'ls-faq-item--open': openFaqIds[i] }"
                            >
                              <button
                                type="button"
                                class="ls-faq-item__head"
                                @click="toggleFaq(i)"
                                :aria-expanded="
                                  openFaqIds[i] ? 'true' : 'false'
                                "
                              >
                                <span class="ls-faq-item__title">
                                  {{
                                    page.Heading ||
                                    page.heading ||
                                    $t('Views.Stage.Labels.information')
                                  }}
                                </span>
                                <i
                                  class="fas"
                                  :class="
                                    openFaqIds[i] ? 'fa-minus' : 'fa-plus'
                                  "
                                  aria-hidden="true"
                                ></i>
                              </button>
                              <div
                                v-if="openFaqIds[i]"
                                class="ls-faq-item__body html-content"
                                v-html="page.Tekst || page.Text || page.text"
                              />
                            </div>
                          </div>

                          <div
                            v-if="
                              !eventName && !eventAbout && !eventPages.length
                            "
                            class="ls-empty"
                          >
                            <i
                              class="far fa-calendar"
                              style="
                                font-size: 28px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.LiveStream.noEventDetails') }}</p>
                          </div>
                        </div>
                      </div>

                      <!-- ── SESSIONS tab ─────────────────────────── -->
                      <div
                        v-else-if="activeTab === 'sessions'"
                        class="ls-panel ls-panel--scroll"
                        id="ls-panel-sessions"
                        role="tabpanel"
                        aria-labelledby="ls-tab-sessions"
                      >
                        <div class="ls-sessions-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-sessions-header__title">
                                {{ $t('Views.LiveStream.sessions') }}
                              </h3>
                              <p class="ls-sessions-header__sub">
                                {{
                                  $tc(
                                    'Views.LiveStream.sessionCount',
                                    filteredSessions.length,
                                    {
                                      n: filteredSessions.length
                                    }
                                  )
                                }}
                              </p>
                            </div>
                          </div>
                          <div class="ls-side-search">
                            <i class="fas fa-search ls-side-search__icon"></i>
                            <input
                              v-model="sessionSearch"
                              type="text"
                              class="ls-side-search__input"
                              :placeholder="
                                $t('Views.LiveStream.searchSessions')
                              "
                              :aria-label="
                                $t('Views.LiveStream.searchSessions')
                              "
                            />
                          </div>
                        </div>
                        <ul class="ls-sessions-list" role="list">
                          <li
                            v-for="s in filteredSessions"
                            :key="s.id"
                            class="ls-session-row"
                            :class="{
                              'ls-session-row--current': isCurrentSession(s)
                            }"
                          >
                            <div class="ls-session-row__time">
                              <span class="ls-session-row__date">{{
                                formatSessionDate(s.startDate)
                              }}</span>
                              <span class="ls-session-row__time-from">{{
                                formatSessionTime(s.startDate)
                              }}</span>
                              <span class="ls-session-row__time-to">{{
                                formatSessionTime(s.endDate)
                              }}</span>
                            </div>
                            <div class="ls-session-row__body">
                              <div class="ls-session-row__title">
                                {{ s.title }}
                              </div>
                              <div
                                v-if="s.subtitle"
                                class="ls-session-row__subtitle"
                              >
                                {{ s.subtitle }}
                              </div>
                              <div
                                v-if="sessionSpeakerNames(s)"
                                class="ls-session-row__speakers"
                              >
                                <i class="fas fa-microphone"></i>
                                {{ sessionSpeakerNames(s) }}
                              </div>
                            </div>
                            <span
                              v-if="isCurrentSession(s)"
                              class="ls-session-row__live-dot"
                              :aria-label="$t('Views.LiveStream.liveNow')"
                            ></span>
                          </li>
                          <li v-if="!filteredSessions.length" class="ls-empty">
                            <i
                              class="far fa-calendar-times"
                              style="
                                font-size: 28px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.LiveStream.noSessions') }}</p>
                          </li>
                        </ul>
                      </div>

                      <!-- ── SPEAKERS tab ─────────────────────────── -->
                      <div
                        v-else-if="activeTab === 'speakers'"
                        class="ls-panel ls-panel--scroll"
                        id="ls-panel-speakers"
                        role="tabpanel"
                        aria-labelledby="ls-tab-speakers"
                      >
                        <div class="ls-side-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-side-header__title">
                                {{ $t('Views.Stage.Labels.speakers') }}
                              </h3>
                              <p class="ls-side-header__sub">
                                {{
                                  $tc(
                                    'Views.LiveStream.speakerCount',
                                    filteredSpeakers.length,
                                    {
                                      n: filteredSpeakers.length
                                    }
                                  )
                                }}
                              </p>
                            </div>
                          </div>
                          <div class="ls-side-search">
                            <i class="fas fa-search ls-side-search__icon"></i>
                            <input
                              v-model="speakerSearch"
                              type="text"
                              class="ls-side-search__input"
                              :placeholder="
                                $t('Views.LiveStream.searchSpeakers')
                              "
                              :aria-label="
                                $t('Views.LiveStream.searchSpeakers')
                              "
                            />
                          </div>
                          <div
                            v-if="
                              features.speakerFilter &&
                              speakerCategoryOptions.length
                            "
                            class="ls-side-filter"
                          >
                            <select
                              v-model="speakerCategoryFilter"
                              class="ls-side-filter__select"
                              :aria-label="
                                $t('Views.LiveStream.filterSpeakersByCategory')
                              "
                            >
                              <option :value="null">
                                {{ $t('Views.LiveStream.allCategories') }}
                              </option>
                              <option
                                v-for="cat in speakerCategoryOptions"
                                :key="cat.value"
                                :value="cat.value"
                              >
                                {{ cat.label }}
                              </option>
                            </select>
                            <i
                              class="fas fa-chevron-down ls-side-filter__chevron"
                            ></i>
                          </div>
                        </div>
                        <ul class="ls-speakers-list" role="list">
                          <li
                            v-for="sp in filteredSpeakers"
                            :key="sp.id || sp.UserID"
                            class="ls-speaker-row"
                            @click="openSpeakerInfo(sp)"
                          >
                            <Avatar
                              :image="sp.profileImageUrl"
                              :circle="true"
                              :name="speakerFullName(sp)"
                              :alt="speakerFullName(sp)"
                              class="ls-speaker-row__avatar"
                            />
                            <div class="ls-speaker-row__info">
                              <div class="ls-speaker-row__name">
                                {{ speakerFullName(sp) }}
                              </div>
                              <div
                                v-if="sp.jobTitle"
                                class="ls-speaker-row__title"
                              >
                                {{ sp.jobTitle }}
                              </div>
                            </div>
                            <i
                              class="fas fa-chevron-right ls-speaker-row__chevron"
                            ></i>
                          </li>
                          <li v-if="!filteredSpeakers.length" class="ls-empty">
                            <i
                              class="fas fa-microphone"
                              style="
                                font-size: 26px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.LiveStream.noSpeakersMatch') }}</p>
                          </li>
                        </ul>
                      </div>

                      <!-- ── SPONSORS tab ─────────────────────────── -->
                      <div
                        v-else-if="activeTab === 'sponsors'"
                        class="ls-panel ls-panel--scroll"
                        id="ls-panel-sponsors"
                        role="tabpanel"
                        aria-labelledby="ls-tab-sponsors"
                      >
                        <div class="ls-side-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-side-header__title">
                                {{ $t('Views.Stage.Labels.sponsors') }}
                              </h3>
                              <p class="ls-side-header__sub">
                                {{
                                  $tc(
                                    'Views.LiveStream.sponsorCount',
                                    filteredSponsors.length,
                                    {
                                      n: filteredSponsors.length
                                    }
                                  )
                                }}
                              </p>
                            </div>
                          </div>
                          <div class="ls-side-search">
                            <i class="fas fa-search ls-side-search__icon"></i>
                            <input
                              v-model="sponsorSearch"
                              type="text"
                              class="ls-side-search__input"
                              :placeholder="
                                $t('Views.LiveStream.searchSponsors')
                              "
                              :aria-label="
                                $t('Views.LiveStream.searchSponsors')
                              "
                            />
                          </div>
                          <div
                            v-if="sponsorCategoryOptions.length"
                            class="ls-side-filter"
                          >
                            <select
                              v-model="sponsorCategoryFilter"
                              class="ls-side-filter__select"
                              :aria-label="
                                $t('Views.LiveStream.filterSponsorsByCategory')
                              "
                            >
                              <option :value="null">
                                {{ $t('Views.LiveStream.allCategories') }}
                              </option>
                              <option
                                v-for="cat in sponsorCategoryOptions"
                                :key="cat.key"
                                :value="cat.key"
                              >
                                {{ cat.label }}
                              </option>
                            </select>
                            <i
                              class="fas fa-chevron-down ls-side-filter__chevron"
                            ></i>
                          </div>
                        </div>
                        <ul class="ls-sponsors-list" role="list">
                          <li
                            v-for="sp in filteredSponsors"
                            :key="sp.id || sp.Id"
                            class="ls-sponsor-row"
                            @click="openSponsorInfo(sp)"
                          >
                            <div class="ls-sponsor-row__logo-wrap">
                              <img
                                v-if="sp.logoUrl"
                                :src="sp.logoUrl"
                                :alt="
                                  sp.name ||
                                  sp.companyName ||
                                  $t('Views.LiveStream.sponsor')
                                "
                                class="ls-sponsor-row__logo"
                              />
                              <span
                                v-else
                                class="ls-sponsor-row__logo-fallback"
                              >
                                <i class="far fa-building"></i>
                              </span>
                            </div>
                            <div class="ls-sponsor-row__info">
                              <div class="ls-sponsor-row__name">
                                {{
                                  sp.name ||
                                  sp.companyName ||
                                  $t('Views.LiveStream.sponsor')
                                }}
                              </div>
                            </div>
                            <i
                              class="fas fa-chevron-right ls-sponsor-row__chevron"
                            ></i>
                          </li>
                          <li v-if="!filteredSponsors.length" class="ls-empty">
                            <i
                              class="fas fa-star"
                              style="
                                font-size: 26px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>{{ $t('Views.LiveStream.noSponsorsMatch') }}</p>
                          </li>
                        </ul>
                      </div>

                      <!-- ── INTERACTION tab (admin-configured webpage) ── -->
                      <div
                        v-else-if="activeTab === 'interaction'"
                        class="ls-panel ls-panel--interaction"
                        id="ls-panel-interaction"
                        role="tabpanel"
                        aria-labelledby="ls-tab-interaction"
                      >
                        <div class="ls-side-header ls-section-header">
                          <div class="ls-section-header__row">
                            <button
                              type="button"
                              class="ls-section-back"
                              @click="activeTab = null"
                              :aria-label="
                                $t('Views.LiveStream.backToAllSections')
                              "
                            >
                              <i
                                class="fas fa-arrow-left"
                                aria-hidden="true"
                              ></i>
                            </button>
                            <div class="ls-section-header__titles">
                              <h3 class="ls-side-header__title">
                                {{ interactionCaption }}
                              </h3>
                              <p class="ls-side-header__sub">
                                {{ $t('Views.LiveStream.interactiveContent') }}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div class="ls-interaction-body">
                          <iframe
                            v-if="getEvent && getEvent.gamificationUrl"
                            :src="getEvent.gamificationUrl"
                            class="ls-interaction-iframe"
                            frameborder="0"
                            tabindex="0"
                            :title="interactionCaption"
                            allow="autoplay; fullscreen; camera; microphone; clipboard-read; clipboard-write"
                          ></iframe>
                          <div v-else class="ls-empty">
                            <i
                              class="far fa-window-restore"
                              style="
                                font-size: 26px;
                                color: #d1d5db;
                                margin-bottom: 8px;
                              "
                            ></i>
                            <p>
                              {{ $t('Views.LiveStream.noInteractionPage') }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- EMPTY STATE — when no section is active and the
                         admin hasn't promoted a message, prompt the user
                         to open the menu via the hamburger button. -->
                      <div
                        v-if="!activeTab && !hasPromotedMessage"
                        class="ls-side-empty"
                      >
                        <div class="ls-side-empty__icon">
                          <i class="fas fa-bars" aria-hidden="true"></i>
                        </div>
                        <h3 class="ls-side-empty__title">
                          {{ $t('Views.LiveStream.exploreEvent') }}
                        </h3>
                        <p class="ls-side-empty__copy">
                          {{ $t('Views.LiveStream.exploreEventCopy') }}
                        </p>
                        <button
                          type="button"
                          class="ls-side-empty__cta ls-side-empty__cta--mobile"
                          @click.stop="$root.$emit('navbar:open-mobile-menu')"
                        >
                          {{ $t('Views.LiveStream.openMenu') }}
                        </button>
                      </div>
                    </div>

                    <!-- (In-card hamburger removed — the Navbar's
                         mobile menu now surfaces this page's section
                         tabs so there's only one hamburger on screen.) -->

                    <!-- DRAWER BACKDROP — only rendered (and only matters)
                         on mobile when the rail drawer is open. -->
                    <transition name="ls-drawer">
                      <div
                        v-if="sidebarOpen"
                        class="ls-drawer-backdrop"
                        @click="sidebarOpen = false"
                      ></div>
                    </transition>

                    <!-- ICON RAIL — always rendered.
                         • Desktop (>1100px): permanent left column,
                           transparent rail with hover-tooltip pills.
                         • Mobile (≤1100px): hidden by default, slides
                           in from the right as a labeled drawer when
                           `sidebarOpen` flips to true. -->
                    <nav
                      class="ls-icon-bar"
                      role="navigation"
                      :aria-label="$t('Views.LiveStream.sectionsNav')"
                    >
                      <button
                        v-for="item in availableMenuItems"
                        :key="`icon-${item.key}`"
                        type="button"
                        class="ls-icon-bar__btn"
                        :class="{
                          'ls-icon-bar__btn--active': activeTab === item.key,
                          'ls-icon-bar__btn--posts': item.key === 'chat'
                        }"
                        :aria-label="item.label"
                        @click="
                          activeTab = activeTab === item.key ? null : item.key;
                          sidebarOpen = false;
                        "
                      >
                        <i :class="item.icon" aria-hidden="true"></i>
                        <span class="ls-icon-bar__label">{{ item.label }}</span>
                      </button>
                    </nav>
                  </div>
                </div>
              </DocumentsDataProvider>
            </StreamDataProvider>
          </AgendasDataProvider>
        </BookSessionsDataProvider>
      </div>

      <div v-else class="ls-loading">
        <i
          class="fas fa-circle-notch fa-spin"
          style="font-size: 24px; color: var(--color-primary, #047873)"
        ></i>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import Navbar from '@/components/shared/Navbar';
import Avatar from '@/components/shared/Avatar';
import StageStream from '@/components/shared/StageStream';
import SpeakerInfoModal from '@/components/SpeakerInfoModal';
import SponsorInfoModal from '@/components/SponsorInfoModal';
import TimelinePromotedItem from '@/components/timeline/TimelinePromotedItem';
import ApiClient from '@/client';
// Promoted-page renderer for inline poll rendering inside the
// promoted message area (poll/quiz/treasure-hunt all share the
// same component on the legacy stage view).
import Poll from '@/components/timeline/promoted/Poll';
import {
  parsePromoLink,
  getApplicablePlatforms,
  getPusherInstance
} from '@/utils';
import {
  listenClickInsideContainer,
  removeClickInsideContainerListener
} from '@/helpers/content-event-helpers';
import participantVisibility from '@/mixins/participantVisibility';

// People tab page size. Kept modest: each page also renders full cards.
const PEOPLE_PAGE_SIZE = 50;

export default {
  name: 'LiveStream',
  mixins: [participantVisibility],
  components: { Navbar, Avatar, StageStream, TimelinePromotedItem, Poll },
  data() {
    return {
      activeTab: null,
      chatInput: '',
      sendingChat: false,
      muted: false,
      replyTarget: null,
      chatPollTimer: null,
      peopleSearch: '',
      // Server-side paging for the People tab. `peopleQuery` is the trimmed
      // search the loaded list was fetched with.
      peopleQuery: '',
      peoplePage: 1,
      peopleSearchTimer: null,
      // Which participant card has its contact details expanded.
      expandedPersonId: null,
      pollClickListener: null,
      // { name, size, dataUrl, kind: 'image' | 'video' | 'file' }
      pendingAttachment: null,
      // Side-panel category filters (null = all)
      speakerCategoryFilter: null,
      sponsorCategoryFilter: null,
      // Side-panel free-text search (Sessions / Speakers / Sponsors)
      sessionSearch: '',
      speakerSearch: '',
      sponsorSearch: '',
      // In-flight email-document requests (by DocumentID) so the
      // "Email me" button can show a spinner + dedupe rapid clicks.
      isEmailingAllDocs: false,
      // Post interactions (Posts tab) ────────────────────────────
      // Which posts have their comment thread expanded.
      openCommentPostIds: [],
      // Per-post comment draft text, keyed by TimelineID.
      commentDrafts: {},
      // Optimistic like state override, keyed by TimelineID (true =
      // liked, false = not), so the thumb reacts before the reload.
      likeOverrides: {},
      // In-flight guards (by TimelineID) to dedupe rapid taps.
      likingPostIds: [],
      commentingPostIds: [],
      deletingPostIds: [],
      // Which post's "⋯" overflow menu is open.
      openActionMenuId: null,
      // Posts the user has deleted this session — filtered out of the
      // list so a stale in-flight reload can't resurrect them.
      deletedPostIds: [],
      // FAQ collapsible state — keyed by index into eventPages.
      openFaqIds: {},
      // Bumping this counter re-runs isOnWebapp which
      // reads localStorage (non-reactive by default). The mounted
      // hook listens for `storage` events and the route watcher
      // bumps it on navigation so a profile change in another tab
      // (or the same tab) is picked up immediately.
      participationTick: 0,
      // Mobile-only "Hide stream" toggle — lets attendees collapse
      // the video player so the side section takes the full screen.
      streamHidden: false,
      // Drawer-style icon rail — collapsed to a hamburger button by
      // default; clicking the hamburger flips this to true and the
      // rail slides in as an overlay inside the side card.
      sidebarOpen: false,
      // When a promoted-message button carries an id alongside the
      // section type (e.g. an Info button targeting one specific
      // FAQ page), we stash that id here so the section panel can
      // scroll to / expand the matching item once it mounts.
      pendingTargetItemId: null,
      // Per-user set of polls the attendee has already answered.
      // Persisted in localStorage as a safety net so re-clicking a
      // promoted poll shows a "you've already answered" view instead
      // of the form, even when the backend doesn't return
      // isAnswered/selectedAnswer reliably.
      answeredPollIds: [],
      // When set, the side panel shows the "already answered"
      // message in place of the live Poll component.
      alreadyAnsweredPollId: null,
      // Emoji picker state for the Post composer.
      emojiPickerOpen: false,
      emojiPalette: [
        '😀',
        '😁',
        '😂',
        '🤣',
        '😊',
        '😍',
        '😘',
        '😎',
        '🤩',
        '🥳',
        '😉',
        '😋',
        '😜',
        '🤔',
        '🙃',
        '😴',
        '😇',
        '🤗',
        '🙏',
        '👍',
        '👎',
        '👏',
        '🙌',
        '💪',
        '✌️',
        '🤝',
        '👋',
        '🔥',
        '✨',
        '🎉',
        '🎊',
        '💯',
        '💖',
        '❤️',
        '🧡',
        '💛',
        '💚',
        '💙',
        '💜',
        '🤍',
        '⭐',
        '🌟',
        '💡',
        '📢',
        '📣',
        '✅',
        '❌',
        '⚡',
        '🚀',
        '🏆'
      ]
    };
  },
  computed: {
    ...mapGetters('CurrentEvent', { getEvent: 'getEvent' }),
    ...mapGetters('CurrentEvent', [
      'features',
      'participantVisibilityMode',
      'isWebappEnabled',
      'isLobbyEnabled',
      'isStageEnabled'
    ]),
    ...mapGetters('Authentication', { profile: 'getUser' }),
    ...mapGetters('Timeline', { promotedPageState: 'getPromotedPage' }),
    ...mapGetters('Stream', {
      streamSessionId: 'getSessionId',
      streamTitle: 'getStreamTitle'
    }),
    ...mapGetters('Agendas', ['getSessionById']),
    ...mapGetters('Agendas', { getAllSessions: 'getSessions' }),
    ...mapGetters('Speakers', { getSpeakersList: 'getItems' }),
    ...mapGetters('Sponsors', { getSponsorsList: 'getItems' }),
    ...mapGetters('Categories', {
      speakersCategoriesList: 'getSpeakersCategories',
      sponsorsCategoriesList: 'getSponsorsCategories',
      allCategoriesList: 'getItems'
    }),
    ...mapGetters('Pages', { getPagesList: 'getItems' }),
    // ── Platform context (Physical = web-app, Digital = digital) ─
    // Same rule as every other screen (getApplicablePlatforms): the
    // event setup first, then the attendee's stored choice on hybrid
    // events. See resolveParticipationType in redirection-helpers.
    isOnWebapp() {
      // eslint-disable-next-line no-unused-vars
      const _ = this.participationTick; // touch to force reactivity
      return getApplicablePlatforms(this.$route).includes('webApp');
    },
    // 1-on-1 chat — uses webAppChat on physical events, stageChat on
    // digital ones. Hidden when the relevant toggle is off.
    isPrivateChatEnabled() {
      const f = this.features || {};
      return this.isOnWebapp ? !!f.webAppChat : !!f.stageChatEnabled;
    },
    // Whether attendees may comment on posts — driven by the admin's
    // timeline-commenting toggle for the current platform. Defaults to
    // enabled unless explicitly turned off.
    isTimelineCommentingEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? f.webAppTimelineCommenting !== false
        : f.stageFuncTimelineCommenting !== false;
    },
    // Whether attendees may create posts — driven by the admin's
    // timeline-posting toggle for the current platform. Defaults to
    // enabled unless explicitly turned off (mirrors commenting).
    isTimelinePostingEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? f.webAppTimelinePosting !== false
        : f.stageFuncTimelinePosting !== false;
    },
    // Per-feature visibility for each side-pane tab. Each one looks
    // at exactly one admin toggle (the one for the current platform
    // context). Mirrors the same pattern Stage.vue uses for its tabs.
    isPostEnabled() {
      const f = this.features || {};
      const timelineOn = this.isOnWebapp
        ? !!f.webAppTimeline
        : !!f.stageTabTimeline;
      // Hide Posts when participants can neither post nor comment. The
      // promoted message is unaffected (it is not part of this tab).
      return (
        timelineOn &&
        (this.isTimelinePostingEnabled || this.isTimelineCommentingEnabled)
      );
    },
    isInformationEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? !!f.webAppNavigationInfoPages
        : !!f.stageTabInfoPages;
    },
    isDocumentsEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? !!f.webAppNavigationDocuments
        : !!f.stageTabDocuments;
    },
    isSponsorsEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? !!f.webAppNavigationSponsors
        : !!f.stageTabSponsors;
    },
    isSpeakersEnabled() {
      const f = this.features || {};
      return this.isOnWebapp
        ? !!f.webAppNavigationSpeakers
        : !!f.stageTabSpeakers;
    },
    isSessionsEnabled() {
      const f = this.features || {};
      return this.isOnWebapp ? !!f.webAppNavigationAgenda : !!f.stageTabAgenda;
    },
    isParticipantsTabEnabled() {
      const f = this.features || {};
      // Combine the platform toggle with the participant-visibility
      // mode so "Not visible" still suppresses the tab even if the
      // admin's web-app/digital toggle is on.
      const platformOn = this.isOnWebapp
        ? !!f.webAppNavigationParticipants
        : !!f.stageTabParticipants;
      return platformOn && this.participantVisibilityMode !== 'hidden';
    },
    isPollEnabled() {
      // If the admin has actually promoted a poll/quiz/treasure-hunt
      // to live, surface the Poll tab unconditionally. Without this
      // override, events where the chat toggle is off would never
      // expose promoted polls — even though the admin clearly meant
      // attendees to see them.
      if (this.actualPollItems && this.actualPollItems.length > 0) {
        return true;
      }
      const f = this.features || {};
      // Fall back to the chat-toggle heuristic when nothing is
      // currently promoted (no dedicated poll flag in the features
      // payload — polls ride the timeline / chat channel).
      return this.isOnWebapp ? !!f.webAppChat : !!f.stageChatEnabled;
    },
    // Master menu definition — each entry carries an `enabled`
    // function so the menu only renders sections the admin has
    // turned on for the current platform (Physical/Web-app vs
    // Digital). Order matches the desired vertical stack.
    menuItems() {
      // Poll is intentionally NOT in the sidebar — admin-promoted
      // polls surface inline in the promoted message at the top of
      // the side card and that's the only place attendees see them.
      return [
        {
          key: 'chat',
          label: this.$t('Views.LiveStream.tabPost'),
          icon: 'far fa-comment',
          enabled: () => this.isPostEnabled
        },
        {
          key: 'eventInfo',
          label: this.$t('Views.Stage.Labels.information'),
          icon: 'far fa-info-circle',
          enabled: () => this.isInformationEnabled
        },
        {
          key: 'docs',
          label: this.$t('Views.Stage.Labels.documents'),
          icon: 'far fa-file-alt',
          enabled: () => this.isDocumentsEnabled
        },
        {
          key: 'sponsors',
          label: this.$t('Views.Stage.Labels.sponsors'),
          icon: 'fas fa-star',
          enabled: () => this.isSponsorsEnabled
        },
        {
          key: 'speakers',
          label: this.$t('Views.Stage.Labels.speakers'),
          icon: 'fas fa-microphone',
          enabled: () => this.isSpeakersEnabled
        },
        {
          key: 'people',
          label: this.$t('Views.Stage.Participants.participants'),
          icon: 'far fa-user',
          enabled: () => this.isParticipantsTabEnabled
        },
        {
          key: 'interaction',
          label: this.interactionCaption,
          icon: 'far fa-window-restore',
          enabled: () => this.isInteractionEnabled
        }
      ];
    },
    isInteractionEnabled() {
      const f = this.features || {};
      const ev = this.getEvent || {};
      return !!(f.stageTabCustom && ev.gamificationUrl);
    },
    interactionCaption() {
      const ev = this.getEvent || {};
      const caps = (ev && ev.captions) || {};
      return caps.gamificationCaption || this.$t('Views.Stage.Labels.timeline');
    },
    availableMenuItems() {
      return this.menuItems.filter((m) => {
        try {
          return m.enabled();
        } catch (e) {
          return true;
        }
      });
    },
    activeTabLabel() {
      const found = this.menuItems.find((m) => m.key === this.activeTab);
      return found ? found.label : '';
    },
    // ── Information tab helpers ─────────────────────────────────
    eventName() {
      const ev = this.getEvent || {};
      return ev.eventName || ev.Title || ev.name || ev.title || '';
    },
    eventAbout() {
      const ev = this.getEvent || {};
      // The event payload uses different field names across
      // versions and platform variants — try every common one.
      return (
        ev.aboutOnlineEvent ||
        ev.aboutPhysicalEvent ||
        ev.about ||
        ev.description ||
        ev.eventDescription ||
        ev.eventAbout ||
        ev.summary ||
        ev.intro ||
        ev.body ||
        ''
      );
    },
    eventLocation() {
      const ev = this.getEvent || {};
      return (
        ev.location ||
        ev.physicalLocation ||
        ev.venue ||
        ev.address ||
        ev.eventLocation ||
        ev.meetingLocation ||
        ev.locationName ||
        ev.place ||
        ''
      );
    },
    eventDateLabel() {
      const ev = this.getEvent || {};
      const start = ev.startDate || ev.eventStartDate;
      const end = ev.endDate || ev.eventEndDate;
      if (!start) return '';
      const s = this.formatEventDate(start);
      const e = end ? this.formatEventDate(end) : '';
      if (e && e !== s) return `${s} – ${e}`;
      return s;
    },
    // ── Sessions tab helpers ────────────────────────────────────
    flatSessions() {
      // The Agendas/getSessions getter returns a flat list of session
      // objects already enriched with speaker references. Normalise
      // the result so the panel can sort by start time without nulls.
      const list = Array.isArray(this.getAllSessions)
        ? this.getAllSessions
        : Object.values(this.getAllSessions || {});
      return list
        .filter((s) => s && s.startDate)
        .slice()
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    },
    // ── Speakers tab helpers ────────────────────────────────────
    activeSpeakers() {
      const list = Array.isArray(this.getSpeakersList)
        ? this.getSpeakersList
        : Object.values(this.getSpeakersList || {});
      return list.filter((s) => s && s.active !== false);
    },
    speakerCategoryOptions() {
      // The speaker list endpoint doesn't return per-speaker
      // categories, so filtering is done server-side (see the
      // `speakerCategoryFilter` watcher). Each option therefore
      // carries the category's server `value` — that's what the
      // backend `category` query param matches on.
      const typed = Array.isArray(this.speakersCategoriesList)
        ? this.speakersCategoriesList
        : [];
      const seen = new Map();
      typed.forEach((c) => {
        if (!c) return;
        const value = this.speakerCategoryValue(c);
        if (value === '') return;
        const label = this.pickCategoryLabel(c) || value;
        // Dedup by the category's unique identity (id), not by `value`: two
        // distinct speaker categories can share or lack a `value`, and keying
        // on `value` collapsed them into a single dropdown option — the
        // "only one of two categories shows" bug. The option keeps the server
        // `value` so backend category filtering still matches.
        const key = this.pickCategoryId(c) || String(value);
        if (!seen.has(key)) {
          seen.set(key, { value: String(value), label });
        }
      });
      return Array.from(seen.values());
    },
    filteredSpeakers() {
      // Category filtering happens server-side; apply the free-text search
      // client-side on top of the (already filtered) active speaker list.
      const q = (this.speakerSearch || '').trim().toLowerCase();
      if (!q) return this.activeSpeakers;
      return this.activeSpeakers.filter((s) =>
        this.matchesSearch(q, [
          this.speakerFullName(s),
          s && s.jobTitle,
          s && s.company
        ])
      );
    },
    // Sessions narrowed by the free-text search (title / subtitle / speaker).
    filteredSessions() {
      const q = (this.sessionSearch || '').trim().toLowerCase();
      if (!q) return this.flatSessions;
      return this.flatSessions.filter((s) =>
        this.matchesSearch(q, [
          s && s.title,
          s && s.subtitle,
          this.sessionSpeakerNames(s)
        ])
      );
    },
    // ── Sponsors tab helpers ────────────────────────────────────
    allSponsors() {
      const list = Array.isArray(this.getSponsorsList)
        ? this.getSponsorsList
        : Object.values(this.getSponsorsList || {});
      return list.slice().sort((a, b) => {
        const oa = parseInt(a && a.order) || 0;
        const ob = parseInt(b && b.order) || 0;
        return oa - ob;
      });
    },
    sponsorCategoryOptions() {
      const typed = Array.isArray(this.sponsorsCategoriesList)
        ? this.sponsorsCategoriesList
        : [];
      const all = Array.isArray(this.allCategoriesList)
        ? this.allCategoriesList
        : [];
      const source = typed.length ? typed : all;
      return this.buildCategoryOptions(source, this.allSponsors);
    },
    filteredSponsors() {
      const key = this.sponsorCategoryFilter
        ? String(this.sponsorCategoryFilter).trim().toLowerCase()
        : null;
      let list = key
        ? this.allSponsors.filter((s) =>
            this.entityCategoryKeys(s).includes(key)
          )
        : this.allSponsors;
      const q = (this.sponsorSearch || '').trim().toLowerCase();
      if (!q) return list;
      return list.filter((s) =>
        this.matchesSearch(q, [s && s.name, s && s.companyName])
      );
    },
    // ── Lookup from category id (any case) → normalized label key
    categoryIdToKeyMap() {
      const map = new Map();
      const all = Array.isArray(this.allCategoriesList)
        ? this.allCategoriesList
        : [];
      all.forEach((c) => {
        if (!c) return;
        const id = this.pickCategoryId(c);
        const label = this.pickCategoryLabel(c);
        if (id && label) {
          map.set(String(id), String(label).trim().toLowerCase());
        }
      });
      return map;
    },
    // ── Information tab — FAQ / pages list ──────────────────────
    eventPages() {
      // Live admin-edited pages (FAQ items like "Future enhancements")
      // attached to the lobby's information section. Filter to ones
      // that have a heading or a body — body field is `Tekst` in
      // the API payload (the Norwegian wording is preserved by the
      // backend), with `Text` / `text` kept as defensive fallbacks.
      const list = Array.isArray(this.getPagesList) ? this.getPagesList : [];
      // Honour each page's admin "Visible in" setting: this surface is the
      // web app or digital — Lobby-only (or the other platform's) pages must
      // not show here. A page with no selection shows everywhere.
      const platform = this.isOnWebapp ? 'webApp' : 'digital';
      return list.filter(
        (p) =>
          p &&
          (p.Heading || p.heading || p.Tekst || p.Text || p.text) &&
          (!Array.isArray(p.PlatformDisplayOptions) ||
            !p.PlatformDisplayOptions.length ||
            p.PlatformDisplayOptions.includes(platform))
      );
    },
    sessionIdFromQuery() {
      return this.$route.query.session || null;
    },
    // The session a post belongs to / is scoped against on THIS page.
    // On the Program (agenda) page there's no single session being
    // watched, so it's empty — posts there are event-wide and no
    // session's posts may leak in. On the live-stream page it's the
    // session actually being streamed. We deliberately ignore the
    // persisted `streamSessionId` on the agenda page so a previously
    // watched session's chat doesn't follow the user back to /program.
    activeSessionId() {
      if (this.$route && this.$route.name === 'program') return '';
      return this.streamSessionId || this.sessionIdFromQuery || '';
    },
    // Description body for the "About this session" block under the
    // player. Falls through every common field name the agenda API
    // surfaces this content under so a non-empty value lands.
    sessionDescriptionHtml() {
      const s = this.currentSession || {};
      const html =
        s.description ||
        s.Description ||
        s.descriptionHtml ||
        s.body ||
        s.Body ||
        s.subtitle ||
        '';
      const text = String(html || '')
        .replace(/<[^>]*>/g, '')
        .trim();
      return text ? html : '';
    },
    currentSession() {
      const id = this.sessionIdFromQuery || this.streamSessionId;
      if (id && this.getSessionById) {
        const s = this.getSessionById(id);
        if (s) return s;
      }
      return {
        title:
          this.streamTitle || (this.getEvent && this.getEvent.eventName) || '',
        speakers: []
      };
    },
    watchingCount() {
      // Use the realtime onlineUserIds list so this count is fully
      // dynamic and scoped to users actively connected to THIS event
      // (the pusher channel is per-event). Falls back to the static
      // total count only if the realtime list is still empty (pusher
      // hasn't connected yet).
      const onlineIds =
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) ||
        [];
      if (onlineIds.length) return onlineIds.length.toLocaleString();
      const total = this.$store.getters['Users/getUserTotalCount'] || 0;
      return total.toLocaleString();
    },
    eventFeatures() {
      return this.$store.getters['CurrentEvent/features'] || {};
    },
    isChatFeatureEnabled() {
      // Default to show. Only hide if every known chat-related flag is
      // explicitly present AND falsy — this respects the admin "1-on-1
      // chat" toggle without accidentally hiding chat for events where
      // the flag names differ or aren't set at all.
      const f = this.eventFeatures || {};
      const keys = [
        'lobbyChatEnabled',
        'stageChatEnabled',
        'webAppChat',
        'lobbyChat',
        'oneToOneChat'
      ];
      const present = keys.filter((k) => k in f);
      if (!present.length) return true;
      // If ANY chat flag is truthy, show.
      return present.some((k) => !!f[k]);
    },
    isPeopleFeatureEnabled() {
      const f = this.eventFeatures || {};
      const keys = [
        'lobbyParticipantList',
        'stageTabParticipants',
        'webAppNavigationParticipants'
      ];
      const present = keys.filter((k) => k in f);
      if (!present.length) return true;
      return present.some((k) => !!f[k]);
    },
    activeCount() {
      // Pull online user IDs straight from state since there's no count getter
      const onlineIds =
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) ||
        [];
      return onlineIds.length.toLocaleString();
    },
    agendaTimezoneMode() {
      return this.getEvent && this.getEvent.agendaTimezoneMode;
    },
    eventTimezone() {
      return this.getEvent && this.getEvent.timezone;
    },
    sessionTimeLabel() {
      const s = this.currentSession;
      if (!s || !s.startDate) return '';
      const start = this.formatSessionTime(s.startDate);
      const end = s.endDate ? this.formatSessionTime(s.endDate) : '';
      const date = this.$moment.utc(s.startDate).local().format('MMM D, YYYY');
      return end ? `${date} • ${start} – ${end}` : `${date} • ${start}`;
    },
    allParticipants() {
      const items =
        (this.$store.state.Users && this.$store.state.Users.items) || [];
      // The Users store can hold either an array or an object keyed by id
      if (Array.isArray(items)) return items;
      return Object.values(items);
    },
    // Search and paging are done by the API, so the store already holds the
    // rows for the current query. Filtering/re-sorting the loaded subset
    // here would hide server matches (e.g. an e-mail hit) and make rows jump
    // each time another page is appended, so the server order is kept.
    filteredParticipants() {
      // 'hidden' fully suppresses the tab (the button is also gated
      // via v-if elsewhere). 'limited' keeps the list visible — the
      // only effect is in detail surfaces (More info / contact)
      // which the visibility mode disables further down the tree.
      if (this.participantVisibilityMode === 'hidden') return [];
      return this.allParticipants.filter((u) => u && u.UserID);
    },
    isPeopleLoading() {
      return (
        !!this.$store.getters['Users/isLoading'] &&
        !this.filteredParticipants.length
      );
    },
    // The API returns no total for a query, so show what is loaded and "+"
    // while a further page may exist (same convention as the lobby list).
    peopleCountLabel() {
      const len = this.filteredParticipants.length;
      const more = this.$store.getters['Users/getHasMore'] ? '+' : '';
      return this.$tc('Views.LiveStream.peopleCount', len, {
        n: `${len}${more}`
      });
    },
    hasPromotedMessage() {
      // True whenever the admin has promoted at least one timeline
      // item (poll, post, etc). Drives the layout switch on the
      // right side of the live-stream page — promoted message takes
      // the main column and the section menu becomes an icon strip.
      return (
        Array.isArray(this.rawPromotedItems) && this.rawPromotedItems.length > 0
      );
    },
    // Active promoted-page state: when an attendee clicks a poll
    // button inside the promoted message, the click delegator sets
    // `Timeline/promotedPage` to `{ page: 'poll', id }`. We render
    // the Poll component inline below so the poll is actually
    // visible and votable from inside the promoted message itself.
    activePromotedPoll() {
      const POLL_PAGES = [
        'poll',
        'quiz',
        'quiz_x',
        'treasure_hunt',
        'coded_polls',
        'coded_quizzes'
      ];
      const p = this.promotedPageState;
      if (p && p.page && p.id && POLL_PAGES.includes(p.page)) {
        return p;
      }
      return null;
    },
    // Only the promoted items that parse to a real poll/quiz link
    // (i.e. admin actually promoted a Poll element from the admin
    // side). Plain-text promoted posts ("Welcome to the event!")
    // do NOT belong in the Poll tab.
    actualPollItems() {
      const POLL_PAGES = [
        'poll',
        'quiz',
        'quiz_x',
        'treasure_hunt',
        'coded_polls',
        'coded_quizzes'
      ];
      return (this.rawPromotedItems || [])
        .map((item) => ({ item, parsed: this.parsePromotedItem(item) }))
        .filter(
          (entry) =>
            entry.parsed &&
            entry.parsed.hasLink &&
            POLL_PAGES.includes(entry.parsed.page)
        );
    },
    rawPromotedItems() {
      // Render promoted timeline items via TimelinePromotedItem so the
      // existing LinkMode / Promolink click handling is reused — the custom
      // parser used to mark cards as non-clickable when admins promoted
      // polls whose link was embedded in the message body instead of the
      // top-level Promolink query string.
      const activeItems =
        this.$store.getters['Timeline/getPromotedTimelineItems'] || [];

      // "Visible after end time" messages: once their end time passes the
      // backend stops returning them as promoted and moves them into the
      // timeline feed (isPromo '1'), so pick them up from there too.
      const activeIds = activeItems.map((item) => item.TimelineID);
      const expiredItems = (
        this.$store.getters['Timeline/getTimelineItems'] || []
      )
        .filter(
          (item) =>
            item &&
            (item.isPromo === '1' || item.isPromo === 1) &&
            !activeIds.includes(item.TimelineID)
        )
        .sort((a, b) => new Date(b.CreatedOn) - new Date(a.CreatedOn));
      const items = [...activeItems, ...expiredItems];

      // Promoted messages belong to the timeline, as in the old platform:
      // hidden when the admin turns Timeline off for this platform (the
      // backend only drops them when it is off for every platform).
      const f = this.features || {};
      const timelineOn = this.isOnWebapp
        ? !!f.webAppTimeline
        : !!f.stageTabTimeline;
      if (!timelineOn) return [];

      // Honour each message's platform targeting (digital / webApp).
      // Messages without targeting show everywhere.
      const platform = this.isOnWebapp ? 'webApp' : 'digital';
      return items.filter(
        (item) =>
          !Array.isArray(item.PlatformDisplayOptions) ||
          !item.PlatformDisplayOptions.length ||
          item.PlatformDisplayOptions.includes(platform)
      );
    }
  },
  mounted() {
    // Arriving on the live-stream page (e.g. from an agenda card's Join
    // button): always start at the top rather than the previous scroll.
    if (this.$route.name === 'live-stream') {
      window.scrollTo(0, 0);
      this.$nextTick(() => window.scrollTo(0, 0));
    }
    // Hydrate the per-user already-answered poll set from
    // localStorage so a re-click of a previously-submitted poll
    // surfaces the "thanks" view straight away.
    this.loadAnsweredPolls();
    // Hydrate the set of locally-deleted posts so they stay hidden
    // after a refresh (the backend may not hard-delete them).
    this.loadDeletedPosts();

    // If we landed on /live-stream with a session id but the Stream store
    // doesn't have a URL yet (e.g. cold reload), force-set it from the session.
    // A session without its own link stays blank (no event-image fallback).
    this.$nextTick(() => {
      const sessionStreamUrl =
        this.currentSession &&
        this.currentSession.id &&
        this.currentSession.liveUrl;
      if (!this.$store.getters['Stream/getStreamUrl'] && sessionStreamUrl) {
        this.$store.commit('Stream/setStreamUrl', sessionStreamUrl);
        this.$store.commit(
          'Stream/setStreamTitle',
          this.currentSession.title || ''
        );
        this.$store.commit('Stream/setSessionId', this.currentSession.id);
      }
    });

    // Make sure attendee counts are populated
    if (!this.$store.getters['Users/getUserTotalCount']) {
      this.$store.dispatch('Users/fetchUserTotalCount').catch(() => {});
    }
    // Always start the People tab from page 1: the shared Users store may
    // hold a partial or filtered list left behind by another screen, and
    // "has anything loaded" says nothing about whether it is complete.
    this.loadPeople(true);
    // Speakers and Sponsors stores aren't auto-populated on this page
    // (their data providers wrap the lobby views). Pull them in here
    // so the Speakers / Sponsors / Sessions side-panels have data.
    if (
      !(
        this.$store.state.Speakers &&
        this.$store.state.Speakers.items &&
        this.$store.state.Speakers.items.length
      )
    ) {
      this.$store.dispatch('Speakers/fetchSpeakers').catch(() => {});
    }
    if (
      !(
        this.$store.state.Sponsors &&
        this.$store.state.Sponsors.items &&
        this.$store.state.Sponsors.items.length
      )
    ) {
      this.$store.dispatch('Sponsors/fetchItems').catch(() => {});
    }
    // Always fetch Categories on mount so the speakers / sponsors
    // dropdown filter has its options populated. The data-provider
    // store's promise cache deduplicates concurrent calls, so this
    // stays cheap.
    this.$store.dispatch('Categories/fetchItems').catch(() => {});
    // Always fetch Pages on mount — the data-provider's promise
    // cache deduplicates concurrent calls, so this stays cheap and
    // ensures the Information tab sees freshly-edited admin pages.
    this.$store.dispatch('Pages/fetchItems').catch(() => {});
    // Pick up profile-page changes to "How do you participate?"
    // even from another tab — `storage` fires on cross-document
    // writes. Refreshing the tick re-runs isOnWebapp.
    this._onStorageEvent = (e) => {
      if (e && e.key && e.key.indexOf('tappin_participation_') === 0) {
        this.participationTick += 1;
      }
    };
    window.addEventListener('storage', this._onStorageEvent);

    // Close the emoji picker when clicking outside it.
    this._onDocClickEmoji = this.onDocClickEmoji.bind(this);
    document.addEventListener('click', this._onDocClickEmoji);

    // Close the post "⋯" overflow menu on any outside click.
    this._onDocClickActionMenu = (e) => {
      if (!this.openActionMenuId) return;
      const t = e && e.target;
      if (t && t.closest && t.closest('.ls-chat-msg__more')) return;
      this.openActionMenuId = null;
    };
    document.addEventListener('click', this._onDocClickActionMenu);

    // Force-load the timeline (regular + promoted) on mount so polls and
    // chat messages are populated even when Pusher isn't connected here.
    this.$store.commit('Timeline/setPromise', null);
    this.loadTimelineData().catch(() => {});

    // Poll the timeline every 5s so new chat messages and newly-
    // promoted polls from the admin show up without a page refresh.
    // Runs regardless of the active tab — promoted messages surface
    // at the top of the side card even when no section is open, so
    // gating the refetch on tab selection would leave attendees
    // staring at stale state until they happened to click Chat/Poll.
    this.chatPollTimer = setInterval(() => {
      this.$store.commit('Timeline/setPromise', null);
      this.loadTimelineData().catch(() => {});
    }, 5000);

    // Tab-change signal from the Navbar's mobile menu. The navbar
    // surfaces our side-panel tabs (Posts / Information / Documents
    // / Sponsors / Speakers / Sessions / Participants) inside its
    // hamburger overlay when the user is on this route, so the
    // in-card hamburger no longer renders. Picking a tab there
    // bubbles up via $root and we flip activeTab here.
    this._onNavbarTabSelect = (key) => {
      // null = back to the home (no section open), emitted by the logo click.
      this.activeTab = key || null;
      // Close the in-card mobile drawer too, in case it was left
      // open from a previous session.
      this.sidebarOpen = false;
    };
    this.$root.$on('live-stream:set-tab', this._onNavbarTabSelect);

    // Navbar home click: if a poll/quiz or side-panel section is open, close it
    // exactly like its own back button does, and tell the navbar it was handled.
    this._onNavbarCloseSection = (done) => {
      const open = !!(this.activePromotedPoll || this.activeTab);
      if (this.activePromotedPoll) this.dismissPromotedPoll();
      this.activeTab = null;
      this.sidebarOpen = false;
      if (typeof done === 'function') done(open);
    };
    this.$root.$on('live-stream:close-section', this._onNavbarCloseSection);

    // If we arrived here from the navbar on another route (e.g.
    // ?tab=speakers / ?tab=eventInfo), open that side-panel tab now —
    // the $root event fired before this view mounted, so honour the query.
    if (this.$route.query && this.$route.query.tab) {
      this._onNavbarTabSelect(this.$route.query.tab);
      this.$root.$emit('live-stream:tab-state', this.activeTab);
    }

    // ── Live content updates ─────────────────────────────────────────
    // The side-panel tabs (Sessions / Information / Documents / Speakers /
    // Sponsors) only fetch once on mount, so admin add/edit/delete never
    // reached them without a manual refresh. Subscribe to the backend's
    // content-change events and force a re-fetch. `refresh` is used for the
    // DataProvider-backed stores because their `fetchItems` short-circuits
    // once data exists; Speakers uses `fetchSpeakers` (no such lock).
    const liveChannel = getPusherInstance({ isReady: true });
    this._liveContentBinds = {
      'agenda-change': () => this.$store.dispatch('Agendas/refresh'),
      'info-page-change': () => this.$store.dispatch('Pages/refresh'),
      'document-change': () => this.$store.dispatch('Documents/refresh'),
      'speaker-change': () => this.$store.dispatch('Speakers/fetchSpeakers'),
      'sponsor-change': () => this.$store.dispatch('Sponsors/refresh')
    };
    Object.keys(this._liveContentBinds).forEach((evt) => {
      liveChannel.bind(evt, this._liveContentBinds[evt]);
    });

    // Attach the promoted-message click delegation now. It binds to
    // `document`, which always exists, so there's no race with the
    // async side-pane render.
    this.attachPollClickListener();
  },
  beforeDestroy() {
    if (this.chatPollTimer) clearInterval(this.chatPollTimer);
    if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
    this.detachPollClickListener();
    if (this._onStorageEvent) {
      window.removeEventListener('storage', this._onStorageEvent);
      this._onStorageEvent = null;
    }
    if (this._onDocClickActionMenu) {
      document.removeEventListener('click', this._onDocClickActionMenu);
      this._onDocClickActionMenu = null;
    }
    if (this._onDocClickEmoji) {
      document.removeEventListener('click', this._onDocClickEmoji);
      this._onDocClickEmoji = null;
    }
    if (this._onNavbarTabSelect) {
      this.$root.$off('live-stream:set-tab', this._onNavbarTabSelect);
      this._onNavbarTabSelect = null;
    }
    if (this._onNavbarCloseSection) {
      this.$root.$off('live-stream:close-section', this._onNavbarCloseSection);
      this._onNavbarCloseSection = null;
    }
    if (this._liveContentBinds) {
      const ch = getPusherInstance({ isReady: true });
      Object.keys(this._liveContentBinds).forEach((evt) => {
        try {
          ch.unbind(evt, this._liveContentBinds[evt]);
        } catch (e) {
          // ignore
        }
      });
      this._liveContentBinds = null;
    }
  },
  watch: {
    // Search is performed by the API (the loaded page is only a slice of
    // the attendees), debounced so typing doesn't fire a request per key.
    peopleSearch() {
      if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
      this.peopleSearchTimer = setTimeout(() => {
        const query = (this.peopleSearch || '').trim();
        if (query === this.peopleQuery) return;
        this.peopleQuery = query;
        this.loadPeople(true);
      }, 350);
    },
    // Switching session keeps this component mounted, so start the
    // scrollable left column back at the player.
    sessionIdFromQuery() {
      if (this.$refs.leftColumn) this.$refs.leftColumn.scrollTop = 0;
    },
    // ── Speaker category filter → server-side refetch ────────────
    // The list endpoint filters by category server-side, so refetch
    // the speakers whenever the selected category changes (empty →
    // full list).
    speakerCategoryFilter(val) {
      this.$store
        .dispatch('Speakers/fetchSpeakers', { category: val || '' })
        .catch(() => {});
    },
    // ── Promoted-page → tab routing ──────────────────────────────
    // The legacy TimelinePromotedItem button (the "Promolinktext"
    // CTA in admin-promoted messages) dispatches `Timeline/
    // setPromotedPage` directly without going through our click
    // delegator. Mirror that state into `activeTab` so those
    // buttons land users in the right side-panel section the same
    // way an in-card data-attribute button does.
    promotedPageState: {
      immediate: false,
      handler(val) {
        if (!val || !val.page) return;
        const TAB_BY_PAGE = {
          info: 'eventInfo',
          infoPage: 'eventInfo',
          infopage: 'eventInfo',
          information: 'eventInfo',
          infoPages: 'eventInfo',
          infopages: 'eventInfo',
          eventInfo: 'eventInfo',
          eventinfo: 'eventInfo',
          // Admin promo links sometimes use these short / variant codes.
          i: 'eventInfo',
          p: 'eventInfo',
          page: 'eventInfo',
          pages: 'eventInfo',
          about: 'eventInfo',
          faq: 'eventInfo',
          sponsor: 'sponsors',
          sponsors: 'sponsors',
          speaker: 'speakers',
          speakers: 'speakers',
          session: 'sessions',
          sessions: 'sessions',
          agenda: 'sessions',
          documents: 'docs',
          document: 'docs',
          docs: 'docs',
          participants: 'people',
          participant: 'people',
          people: 'people'
        };
        // Case-insensitive lookup so `pg=Info` / `pg=INFO` also match.
        const rawKey = String(val.page).trim();
        let targetTab =
          TAB_BY_PAGE[rawKey] || TAB_BY_PAGE[rawKey.toLowerCase()];
        // Last-resort heuristic: if the key contains "info"/"page",
        // route to the Info tab. Covers admin promo links with
        // unexpected key variants (e.g. `pg=infoTab`).
        if (!targetTab) {
          const k = rawKey.toLowerCase();
          if (k.includes('info') || k.includes('page')) targetTab = 'eventInfo';
          else if (k.includes('sponsor')) targetTab = 'sponsors';
          else if (k.includes('speaker')) targetTab = 'speakers';
          else if (k.includes('session') || k.includes('agenda'))
            targetTab = 'sessions';
          else if (k.includes('doc')) targetTab = 'docs';
          else if (k.includes('people') || k.includes('participant'))
            targetTab = 'people';
        }
        // The Sessions tab was removed from the side card.
        if (!targetTab || targetTab === 'sessions') return;
        this.activeTab = targetTab;
        this.sidebarOpen = false;
        if (val.id) {
          this.pendingTargetItemId = String(val.id);
          this.$nextTick(() => this.applyPendingTargetItem());
        }
      }
    },
    // Live admin toggle changes — if the user is currently in a
    // section the admin just disabled (for the current platform
    // context), bounce back to the master menu so they don't see
    // a feature they're no longer entitled to.
    availableMenuItems: {
      immediate: false,
      handler(items) {
        if (!this.activeTab) return;
        // Right after arriving from another page the event features may
        // still be loading — don't read an empty menu as "admin disabled it".
        if (!Array.isArray(items) || !items.length) return;
        if (!Object.keys(this.features || {}).length) return;
        const stillAllowed = items.some((m) => m.key === this.activeTab);
        if (!stillAllowed) this.activeTab = null;
      }
    },
    // Tell the bottom menu which section is really open, so Posts is
    // highlighted whenever the Posts panel shows (e.g. from Networking).
    activeTab(val) {
      this.$root.$emit('live-stream:tab-state', val);
    },
    // Re-apply the tab the navbar asked for (?tab=chat) once Posts becomes
    // available, in case settings resolved after arrival.
    isPostEnabled(on) {
      const want = this.$route.query && this.$route.query.tab;
      if (on && want === 'chat' && !this.activeTab) this.activeTab = 'chat';
    },
    // Route changes (incl. coming back from /profile) — re-read
    // the participation localStorage value in case the user just
    // flipped their Physical/Digital choice on the same tab.
    $route: {
      immediate: false,
      handler() {
        this.participationTick += 1;
        // Re-announce the open section after the navbar's own route
        // handling, so Posts stays highlighted if the address lost ?tab.
        this.$nextTick(() =>
          this.$root.$emit('live-stream:tab-state', this.activeTab)
        );
      }
    }
  },
  methods: {
    ...mapActions('Timeline', [
      'addTimelineEntry',
      'loadTimelineData',
      'setPromotedPage',
      'resetPromotedPage',
      'likePost',
      'unlikePost',
      'addComment',
      'deleteItem',
      'deleteComment'
    ]),
    dismissPromotedPoll() {
      const id = this.activePromotedPoll && this.activePromotedPoll.id;
      this.alreadyAnsweredPollId = null;
      this.resetPromotedPage();
      // Best-effort persistence: when the user backs out, re-check
      // the poll's state and mark it answered so a subsequent click
      // surfaces the "already answered" view instead of the form.
      if (id) {
        ApiClient.getPoll(id, false)
          .then((response) => {
            if (
              response &&
              response.data &&
              this.isResponseAnswered(response.data)
            ) {
              this.markPollAnswered(id);
            }
          })
          .catch(() => {
            /* no-op */
          });
      }
    },
    // ── Already-answered persistence ──────────────────────────────
    answeredPollsStorageKey() {
      const uid = (this.profile && this.profile.UserID) || 'guest';
      return `tappin_polls_answered_${uid}`;
    },
    loadAnsweredPolls() {
      try {
        const raw = localStorage.getItem(this.answeredPollsStorageKey());
        const arr = raw ? JSON.parse(raw) : [];
        this.answeredPollIds = Array.isArray(arr) ? arr.map(String) : [];
      } catch (e) {
        this.answeredPollIds = [];
      }
    },
    saveAnsweredPolls() {
      try {
        localStorage.setItem(
          this.answeredPollsStorageKey(),
          JSON.stringify(this.answeredPollIds)
        );
      } catch (e) {
        /* no-op */
      }
    },
    isPollAnswered(id) {
      if (id === undefined || id === null) return false;
      return this.answeredPollIds.indexOf(String(id)) !== -1;
    },
    markPollAnswered(id) {
      if (id === undefined || id === null) return;
      const key = String(id);
      if (!this.isPollAnswered(key)) {
        this.answeredPollIds = [...this.answeredPollIds, key];
        this.saveAnsweredPolls();
      }
    },
    isResponseAnswered(data) {
      // A poll counts as already-answered when the API tells us
      // either via the boolean flag OR the `selectedAnswer` payload.
      return !!(data && (data.isAnswered === true || data.selectedAnswer));
    },
    attachPollClickListener() {
      // Delegate from `document` rather than the side-pane element.
      // The side pane lives behind `v-if="hasFullEventDetails"` and
      // renders asynchronously, so binding to its ref races the data
      // load (and misses the mobile promoted message, which sits
      // outside the side pane). The delegation only acts on elements
      // carrying promoted-message markers (`internal/…` hrefs or
      // `data-tappin-*` attributes), so a document-level listener is
      // safe — ordinary links elsewhere fall straight through.
      this.detachPollClickListener();
      const el = document;
      this.pollClickListener = listenClickInsideContainer(
        el,
        this.handlePromotedSectionClick,
        this.$gtag,
        this.handlePromotedSectionClick
      );
      this._pollClickListenerEl = el;
      // Plain links the admin typed into info pages / posts without a scheme
      // ("www.site.com") would resolve against this site; make them real
      // external links that open in a new tab.
      this._fixLinkListener = (evt) => {
        const a =
          evt.target && evt.target.closest && evt.target.closest('a[href]');
        if (!a || !a.closest('.html-content')) return;
        const href = (a.getAttribute('href') || '').trim();
        if (!href || /^(internal\/|#|\/|[a-z][a-z0-9+.-]*:)/i.test(href)) {
          if (/^https?:/i.test(href) && !a.target) {
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
          }
          return;
        }
        a.setAttribute('href', `https://${href}`);
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      };
      document.addEventListener('click', this._fixLinkListener, true);
    },
    // Map a `setPromotedPage` / `setInternalLink` payload from the
    // promoted-message click delegator (`listenClickInsideContainer`)
    // onto a sidebar tab in this view. For poll-style pages, fetch
    // the poll first so we know its real type and commit the right
    // page key for the inline Poll renderer.
    // Once the section panel has rendered, expand / scroll-to the
    // specific item the click delegator pointed at (if any). Clears
    // the pending id either way so it doesn't fire again.
    applyPendingTargetItem() {
      const id = this.pendingTargetItemId;
      if (!id) return;
      this.pendingTargetItemId = null;
      if (this.activeTab === 'eventInfo') {
        // Find the FAQ index whose id matches and open it. The live
        // info-pages API serialises the id as `PageID`.
        const idx = (this.eventPages || []).findIndex(
          (p) => String(p.PageID || p.Id || p.id || '') === id
        );
        if (idx >= 0) {
          this.$set(this.openFaqIds, idx, true);
        }
      }
      // Sponsors / Speakers / Sessions / People — scroll the matching
      // row into view if we can find one keyed off `data-id`.
      this.$nextTick(() => {
        const root = this.$refs.sidePane;
        if (!root) return;
        const sel = `[data-id="${CSS.escape(id)}"]`;
        const target = root.querySelector(sel);
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    handlePromotedSectionClick(payload) {
      if (!payload) return;
      let page = payload.page || payload.type;
      // Editor "Link list" values for coded polls / quizzes.
      if (page === 'codedPoll') page = 'coded_polls';
      if (page === 'codedQuiz') page = 'coded_quizzes';
      // Map promoted-message page/type strings to our sidebar tab keys.
      // The user-profile button isn't a sidebar section — it's a
      // standalone route. Send the attendee to their own profile
      // page instead of trying to map it onto an in-card tab.
      if (page === 'userprofile' || page === 'userProfile') {
        this.sidebarOpen = false;
        if (this.$route.name !== 'profile') {
          this.$router.push({ name: 'profile' });
        }
        return;
      }
      // A button for one specific sponsor opens that sponsor's own page
      // (it lands on the Info tab) instead of the all-sponsors list.
      // Without an id (the "All sponsors" button) it falls through to
      // the Sponsors tab below.
      if (String(page).toLowerCase() === 'sponsor' && payload.id) {
        this.sidebarOpen = false;
        if (
          this.$route.name !== 'sponsor' ||
          String(this.$route.params.sponsorId) !== String(payload.id)
        ) {
          this.$router
            .push({ name: 'sponsor', params: { sponsorId: payload.id } })
            .catch(() => {});
        }
        return;
      }
      const TAB_BY_PAGE = {
        info: 'eventInfo',
        infoPage: 'eventInfo',
        infopage: 'eventInfo',
        information: 'eventInfo',
        eventInfo: 'eventInfo',
        eventinfo: 'eventInfo',
        sponsor: 'sponsors',
        sponsors: 'sponsors',
        allSponsors: 'sponsors',
        allsponsors: 'sponsors',
        speaker: 'speakers',
        speakers: 'speakers',
        session: 'sessions',
        sessions: 'sessions',
        agenda: 'sessions',
        agendaSession: 'sessions',
        documents: 'docs',
        document: 'docs',
        docs: 'docs',
        participants: 'people',
        participant: 'people',
        people: 'people'
      };
      const targetTab = TAB_BY_PAGE[page];
      // The Sessions tab was removed from the side card.
      if (targetTab && targetTab !== 'sessions') {
        // The admin explicitly placed this button — honour the
        // click even if the matching menu item is currently hidden
        // by the platform's enabled-state heuristic.
        this.activeTab = targetTab;
        this.sidebarOpen = false;
        // If the click carried a specific item id (e.g. an Info
        // button pointing at a single FAQ page), remember it so
        // the section panel can scroll to / expand that item.
        if (payload.id) {
          this.pendingTargetItemId = String(payload.id);
          this.$nextTick(() => this.applyPendingTargetItem());
        }
        return;
      }
      // Poll / quiz / treasure_hunt etc — fetch the poll so we can
      // derive the canonical page key from the server's `pollType`
      // (admins don't always set a matching `data-tappin-poll-type`).
      const POLL_PAGES = [
        'poll',
        'quiz',
        'quiz_x',
        'treasure_hunt',
        'coded_polls',
        'coded_quizzes'
      ];
      const isCoded = page === 'coded_polls' || page === 'coded_quizzes';
      if (page && POLL_PAGES.includes(page) && (payload.id || isCoded)) {
        this.openPromotedPoll(page, payload.id);
        return;
      }
      // Anything else falls through to the legacy dispatch path.
      if (page) {
        this.setPromotedPage({
          page,
          id: payload.id,
          liveUrl: payload.liveUrl
        });
      }
    },
    // Maps the API's numeric `pollType` to the page key strings that
    // Poll.vue's inner template branches on.
    pollTypeToPageKey(pollType) {
      switch (pollType) {
        case 0:
        case 1:
          return 'poll';
        case 2:
          return 'quiz';
        case 3:
          return 'treasure_hunt';
        default:
          return 'poll';
      }
    },
    openPromotedPoll(rawPage, id) {
      // The poll renders in the promoted area, which only shows when no
      // side-panel tab is open — so a poll link clicked from an info page,
      // post or chat panel must first leave that tab, otherwise the poll is
      // opened but never visible.
      this.activeTab = null;
      this.sidebarOpen = false;
      // Already-answered short-circuit. If the local user has
      // submitted this poll before, surface the "thanks" view
      // straight away instead of mounting the form again.
      if (this.isPollAnswered(id)) {
        this.alreadyAnsweredPollId = String(id);
        this.setPromotedPage({ page: rawPage, id });
        return;
      }
      this.alreadyAnsweredPollId = null;

      // Coded polls/quizzes don't have a single answerable poll —
      // they prompt for a code first. Skip the API fetch and just
      // commit the page so CodedPoll renders.
      if (rawPage === 'coded_polls' || rawPage === 'coded_quizzes') {
        this.setPromotedPage({ page: rawPage, id });
        return;
      }
      // For real polls/quizzes/treasure-hunts, fetch the poll first
      // so we know its true type and can commit a page key that
      // Poll.vue's template will actually render.
      ApiClient.getPoll(id, false)
        .then((response) => {
          if (response && response.data) {
            // Trust the backend if it tells us the user has
            // already answered this poll.
            if (this.isResponseAnswered(response.data)) {
              this.markPollAnswered(id);
              this.alreadyAnsweredPollId = String(id);
            }
            this.setPromotedPage({
              page: this.pollTypeToPageKey(response.data.pollType),
              id: response.data.id || id
            });
          } else {
            // No data — fall back to the raw page so something at
            // least mounts, even if it might be empty.
            this.setPromotedPage({ page: rawPage, id });
          }
        })
        .catch(() => {
          this.setPromotedPage({ page: rawPage, id });
        });
    },
    detachPollClickListener() {
      if (this._fixLinkListener) {
        document.removeEventListener('click', this._fixLinkListener, true);
        this._fixLinkListener = null;
      }
      if (this.pollClickListener && this._pollClickListenerEl) {
        removeClickInsideContainerListener(
          this._pollClickListenerEl,
          this.pollClickListener
        );
      }
      this.pollClickListener = null;
      this._pollClickListenerEl = null;
    },

    speakerNames(session) {
      if (!session || !session.speakers || !session.speakers.length) return '';
      return session.speakers
        .map((s) => `${s.name || ''} ${s.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    },
    isLiveSession(session) {
      if (!session || !session.startDate || !session.endDate) return true; // default true on stream page
      const now = this.$moment();
      const start = this.$moment.utc(session.startDate).local();
      const end = this.$moment.utc(session.endDate).local();
      return now.isSameOrAfter(start) && now.isBefore(end);
    },
    formatSessionTime(date) {
      if (!date) return '';
      if (
        this.agendaTimezoneMode === 2 &&
        this.eventTimezone &&
        this.$moment.tz
      ) {
        return this.$moment.tz(date, this.eventTimezone).format('HH:mm');
      }
      return this.$moment.utc(date).local().format('HH:mm');
    },
    formatEventDate(date) {
      if (!date) return '';
      return this.$moment.utc(date).local().format('MMM D, YYYY');
    },
    toggleFaq(index) {
      // Vue 2 reactivity: must explicitly set a new object so the
      // collapsible state animates.
      this.$set(this.openFaqIds, index, !this.openFaqIds[index]);
    },
    formatSessionDate(date) {
      if (!date) return '';
      // Compact day label for the Sessions panel rows.
      if (
        this.agendaTimezoneMode === 2 &&
        this.eventTimezone &&
        this.$moment.tz
      ) {
        return this.$moment.tz(date, this.eventTimezone).format('MMM D');
      }
      return this.$moment.utc(date).local().format('MMM D');
    },
    // True when the (already lower-cased) `query` is a substring of any of
    // the given fields. Shared by the Sessions / Speakers / Sponsors search.
    matchesSearch(query, fields) {
      return fields.some((v) => v && String(v).toLowerCase().includes(query));
    },
    // Sessions tab helpers ──────────────────────────────────────────
    isCurrentSession(session) {
      // Reuse the existing isLiveSession test (with a sane default of
      // false here so completed past sessions don't get marked live).
      if (!session || !session.startDate || !session.endDate) return false;
      const now = this.$moment();
      const start = this.$moment.utc(session.startDate).local();
      const end = this.$moment.utc(session.endDate).local();
      return now.isSameOrAfter(start) && now.isBefore(end);
    },
    sessionSpeakerNames(session) {
      if (!session || !session.speakers || !session.speakers.length) return '';
      return session.speakers
        .map((s) => `${s.name || ''} ${s.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    },
    // ── Category helpers shared by speakers/sponsors filters ──────
    // Helper: pull a category's display label out of whatever
    // mixed-case / legacy fields the API might use. Falls through
    // every common variant before giving up.
    pickCategoryLabel(c) {
      if (!c) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      return (
        c.label ||
        c.Label ||
        c.value ||
        c.Value ||
        c.name ||
        c.Name ||
        c.title ||
        c.Title ||
        c.caption ||
        c.Caption ||
        c.text ||
        c.Text ||
        c.categoryName ||
        c.CategoryName ||
        c.displayName ||
        c.DisplayName ||
        ''
      );
    },
    // The value the backend `category` filter matches on. Prefer the
    // category's `value` field (what the legacy speaker filter sends),
    // falling back to its id.
    speakerCategoryValue(c) {
      if (c === null || c === undefined) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      if (
        c.value !== undefined &&
        c.value !== null &&
        String(c.value).trim() !== ''
      ) {
        return String(c.value);
      }
      return this.pickCategoryId(c);
    },
    // Helper: pull the category id across the field variants.
    pickCategoryId(c) {
      if (!c) return '';
      if (typeof c === 'string' || typeof c === 'number') return String(c);
      return String(
        c.id ||
          c.Id ||
          c.ID ||
          c.categoryId ||
          c.CategoryId ||
          c.categoryID ||
          c.CategoryID ||
          c.cid ||
          c.Cid ||
          ''
      );
    },
    // Helper: turn whatever an entity carries for "categories" into
    // a flat array. Handles array, single object, bare id, and the
    // 10+ field-name variants the platform has seen historically.
    pickEntityCategories(entity) {
      if (!entity) return [];
      const candidates = [
        entity.speakerCategories,
        entity.SpeakerCategories,
        entity.sponsorCategories,
        entity.SponsorCategories,
        entity.speakerCategoryIds,
        entity.SpeakerCategoryIds,
        entity.sponsorCategoryIds,
        entity.SponsorCategoryIds,
        entity.categoryIds,
        entity.CategoryIds,
        entity.categories,
        entity.Categories,
        entity.category,
        entity.Category,
        entity.categoryId,
        entity.CategoryId
      ];
      // Collect the UNION of every category bucket the entity carries, not
      // just the first non-empty one. A sponsor's "type" lives in
      // `categories` (category objects) while a separate filter bucket lives
      // in `sponsorCategories` (bare ids) — and the dropdown is built from
      // both. If we returned only one bucket, selecting a category from the
      // other one matched no sponsor and the list came back empty. Merging
      // keeps the dropdown options and the match keys in perfect sync, so
      // selecting any category a sponsor actually has filters correctly.
      // (An empty array is truthy in JS, which is why a plain `a || b` chain
      // also failed here — it stopped at an empty `sponsorCategories: []`.)
      const out = [];
      for (const c of candidates) {
        if (c === null || c === undefined) continue;
        if (Array.isArray(c)) {
          c.forEach((v) => {
            if (v !== null && v !== undefined && v !== '') out.push(v);
          });
        } else if (c !== '') {
          out.push(c);
        }
      }
      return out;
    },
    buildCategoryOptions(categoriesFromStore, entityList) {
      // { key, label } collection — keyed by lowercased label so
      // dropdown values line up with `entityCategoryKeys()`. When an
      // entity tag is a bare id, look it up against the categories
      // store so the option carries the human label instead of the id.
      const lookup = this.categoryIdToKeyMap;
      const seen = new Map();
      const all = Array.isArray(this.allCategoriesList)
        ? this.allCategoriesList
        : [];
      const addByLabel = (label) => {
        if (!label) return;
        const key = String(label).trim().toLowerCase();
        if (!key) return;
        if (!seen.has(key)) seen.set(key, { key, label: String(label) });
      };
      const labelById = (id) => {
        const match = all.find(
          (cat) => String(this.pickCategoryId(cat)) === String(id)
        );
        return match ? this.pickCategoryLabel(match) : '';
      };
      const add = (c) => {
        if (c === null || c === undefined) return;
        if (typeof c === 'string' || typeof c === 'number') {
          const str = String(c);
          const friendly = labelById(str);
          addByLabel(friendly || str);
          return;
        }
        const label = this.pickCategoryLabel(c);
        if (label) {
          addByLabel(label);
          return;
        }
        const id = this.pickCategoryId(c);
        if (!id) return;
        addByLabel(labelById(id) || id);
      };
      if (Array.isArray(categoriesFromStore)) {
        categoriesFromStore.forEach(add);
      }
      if (Array.isArray(entityList)) {
        entityList.forEach((e) => {
          this.pickEntityCategories(e).forEach(add);
        });
      }
      return Array.from(seen.values());
    },
    entityCategoryKeys(entity) {
      const raw = this.pickEntityCategories(entity);
      if (!raw.length) return [];
      const lookup = this.categoryIdToKeyMap;
      return raw
        .flatMap((c) => {
          if (c === null || c === undefined) return [];
          // Bare scalar — could be an id OR a label. Emit BOTH so
          // the filter matches whichever form the dropdown ended
          // up keyed on.
          if (typeof c === 'string' || typeof c === 'number') {
            const str = String(c);
            const lower = str.trim().toLowerCase();
            const looked = lookup.get(str) || lookup.get(lower);
            return looked && looked !== lower ? [looked, lower] : [lower];
          }
          const label = this.pickCategoryLabel(c);
          const id = this.pickCategoryId(c);
          const out = [];
          if (label) out.push(String(label).trim().toLowerCase());
          if (id) {
            const looked = lookup.get(String(id));
            if (looked) out.push(looked);
            out.push(String(id).trim().toLowerCase());
          }
          return out;
        })
        .filter(Boolean);
    },
    // Speakers tab helpers ──────────────────────────────────────────
    speakerFullName(speaker) {
      if (!speaker) return '';
      const first = speaker.name || speaker.Firstname || '';
      const last = speaker.lastName || speaker.Lastname || '';
      return `${first} ${last}`.trim();
    },
    openSpeakerInfo(speaker) {
      if (!speaker) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SpeakerInfoModal,
        { speaker },
        {
          width: '92%',
          height: 'auto',
          adaptive: true,
          minWidth: 300,
          maxWidth: 640
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    // Sponsors tab helpers ─────────────────────────────────────────
    openSponsorInfo(sponsor) {
      if (!sponsor) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SponsorInfoModal,
        { sponsor },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    // Fetch a page of attendees for the current search. `reset` starts again
    // from page 1 and replaces the list; otherwise the next page is appended.
    async loadPeople(reset = false) {
      this.peoplePage = reset ? 1 : this.peoplePage + 1;
      await this.$store
        .dispatch('Users/fetchUsers', {
          page: this.peoplePage,
          pageSize: PEOPLE_PAGE_SIZE,
          search: this.peopleQuery,
          groupId: '',
          append: !reset
        })
        .catch(() => {});
      if (reset) {
        if (this.$refs.peopleList) this.$refs.peopleList.scrollTop = 0;
      } else if (this.$store.getters['Users/getFailed']) {
        // Page failed to load: let the next scroll retry the same page.
        this.peoplePage -= 1;
      }
    },
    onPeopleScroll(e) {
      const el = e && e.target;
      if (!el || this.participantVisibilityMode === 'hidden') return;
      if (this.$store.getters['Users/isLoading']) return;
      if (!this.$store.getters['Users/getHasMore']) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.loadPeople(false);
      }
    },
    isPersonOnline(userId) {
      if (!userId) return false;
      const ids =
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) ||
        [];
      return ids.includes(userId);
    },
    // Expand / collapse a participant's contact details (one at a time).
    togglePersonDetails(user) {
      if (!user || !user.UserID) return;
      this.expandedPersonId =
        this.expandedPersonId === user.UserID ? null : user.UserID;
    },
    // ── Accessible status announcements ──────────────────────────────
    announceChatError(msg) {
      // Inject a temporary aria-live region so screen readers announce
      // the error even if the user is focused elsewhere.
      const el = document.createElement('div');
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      el.style.cssText =
        'position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;';
      el.textContent = msg;
      document.body.appendChild(el);
      setTimeout(() => document.body.removeChild(el), 5000);
    },

    // ── Tab keyboard navigation (ARIA Authoring Practices) ──────────
    onTabKeydown(e) {
      const tabs = ['chat', 'docs', 'poll', 'people'];
      const idx = tabs.indexOf(this.activeTab);
      let next = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        next = (idx + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        next = (idx - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = tabs.length - 1;
      } else {
        return;
      }
      e.preventDefault();
      this.activeTab = tabs[next];
      this.$nextTick(() => {
        const btn = document.getElementById(`ls-tab-${tabs[next]}`);
        if (btn) btn.focus();
      });
    },
    goBack() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push('/program');
    },
    toggleMute() {
      this.muted = !this.muted;
      const v = document.getElementById('video-player');
      if (v) v.muted = this.muted;
    },
    toggleFullscreen() {
      const player = document.querySelector('.ls-player');
      if (!player) return;
      if (!document.fullscreenElement) {
        player.requestFullscreen && player.requestFullscreen();
      } else {
        document.exitFullscreen && document.exitFullscreen();
      }
    },

    // ── Chat ───────────────────────────────────────────────────────────
    sessionMarker() {
      // The Timeline API returns messages for the entire event, not for a
      // specific session. We tag every chat message we send with a hidden
      // marker containing the current session id, then filter by it on
      // display so chats from one session don't leak into another.
      const sid = this.activeSessionId;
      return sid ? `<!--tappin-session:${sid}-->` : '';
    },
    isMessageForCurrentSession(item) {
      const msg = (item && item.Message) || '';
      const sid = this.activeSessionId;
      const isSessionTagged = /<!--tappin-session:[^>]+-->/.test(msg);
      const isPromo = item && (item.isPromo === '1' || item.isPromo === 1);
      // Outside any session (e.g. the Program / agenda page): never show
      // posts that belong to a specific session — only event-wide posts
      // and admin promos surface here.
      if (!sid) return !isSessionTagged || isPromo;
      // Inside a session:
      //  • tagged with THIS session → show
      //  • tagged with a DIFFERENT session → hide (the leak case)
      //  • untagged → only admin-promoted announcements show
      if (msg.includes(`<!--tappin-session:${sid}-->`)) return true;
      if (isSessionTagged) return false;
      return isPromo;
    },
    chatMessages(items) {
      // Fall back to raw store items if the filtered list is empty
      // (admin posts may not be tagged with the digital/webApp platform flag)
      const list =
        items && items.length
          ? items
          : this.$store.getters['Timeline/getTimelineItems'] || [];
      return list
        .filter((i) => i && i.Message && i.Message.trim())
        .filter((i) => this.deletedPostIds.indexOf(i.TimelineID) === -1)
        .filter((i) => this.isMessageForCurrentSession(i))
        .slice()
        .sort(
          (a, b) =>
            new Date(a.CreatedOn || a.Tid) - new Date(b.CreatedOn || b.Tid)
        );
    },
    formatChatTime(date) {
      if (!date) return '';
      return Vue.moment(date).format('h:mm A');
    },
    // The backend stamps timeline authors with the localized
    // "Unknown / User" placeholder name (while still giving us the real
    // UserID). Detect that so we don't trust it and instead resolve the
    // real name by id. Also treats a blank name as a placeholder.
    isUnknownAuthorName(o) {
      if (!o) return true;
      const first = String(o.Firstname || o.FirstName || '').trim();
      const last = String(o.Lastname || o.LastName || '').trim();
      if (!first && !last && !o.name) return true;
      if (first === 'Unknown' && last === 'User') return true;
      return (
        first === this.$t('Components.Timeline.unknown') &&
        last === this.$t('Components.Timeline.user')
      );
    },
    // A hidden author marker we embed in messages we post (the timeline
    // API returns `Author: null`, so this is how a post carries its
    // author on this platform). Format: <!--tappin-author:UID|Name-->.
    authorMarker() {
      const me = this.profile || {};
      const uid = me.UserID || '';
      const name =
        `${me.Firstname || me.firstName || me.FirstName || ''} ${
          me.Lastname || me.lastName || me.LastName || ''
        }`.trim() ||
        me.name ||
        '';
      if (!uid && !name) return '';
      return `<!--tappin-author:${uid}|${encodeURIComponent(name)}-->`;
    },
    authorFromMarker(message) {
      if (!message) return null;
      const m = message.match(/<!--tappin-author:([^|]*)\|([^>]*?)-->/);
      if (!m) return null;
      let name = '';
      try {
        name = decodeURIComponent(m[2] || '');
      } catch (e) {
        name = m[2] || '';
      }
      return { UserID: m[1] || '', name };
    },
    // Resolve the author object for a timeline item. The embedded
    // Author/User name is only trusted when it's a real name (not the
    // backend placeholder); otherwise we resolve by UserID — own posts
    // from the signed-in profile, others from the Users store — and as a
    // final fallback, the author marker embedded in the message.
    msgAuthor(item) {
      if (!item) return {};
      const realName = (o) => o && !this.isUnknownAuthorName(o);
      // 1. Embedded author objects carrying a REAL name.
      if (realName(item.Author)) return item.Author;
      if (realName(item.User)) return item.User;
      // 2. Resolve a UserID off the item / its placeholder author / the
      //    embedded author marker.
      const marked = this.authorFromMarker(item.Message);
      const uid =
        item.UserID ||
        item.userId ||
        (item.Author && item.Author.UserID) ||
        (item.User && item.User.UserID) ||
        (marked && marked.UserID);
      // 3. Own post → the signed-in profile (has the real name + avatar).
      const me = this.profile || {};
      if (uid && me.UserID && String(uid) === String(me.UserID)) return me;
      // 4. Users store — a real entry with a real name.
      if (uid) {
        const fromStore = this.$store.getters['Users/getById'](uid);
        if (fromStore && fromStore.UserID && realName(fromStore)) {
          return fromStore;
        }
      }
      // 5. The name carried by the author marker.
      if (marked && marked.name) {
        return { UserID: marked.UserID, Firstname: marked.name };
      }
      // 6. Last resort: the placeholder author object — we still use its
      //    Email (below) / Picture / UserID for a sensible fallback.
      if (item.Author && item.Author.UserID) return item.Author;
      if (item.User && item.User.UserID) return item.User;
      return {};
    },
    msgAuthorName(item) {
      const a = this.msgAuthor(item) || {};
      const first = a.Firstname || a.FirstName || a.firstName || '';
      const last = a.Lastname || a.LastName || a.lastName || '';
      const name = `${first} ${last}`.trim();
      if (name && !this.isUnknownAuthorName(a)) return name;
      // Backend only gave the "Unknown User" placeholder — derive a
      // readable name from the email's local part instead.
      const email = a.Email || a.email || '';
      if (email && email.indexOf('@') > 0) {
        const local = email
          .split('@')[0]
          .replace(/[._-]+/g, ' ')
          .trim();
        if (local) {
          return local.replace(/\b\w/g, (ch) => ch.toUpperCase());
        }
      }
      return a.name || a.Name || this.$t('Views.LiveStream.anonymous');
    },
    isOwnMessage(item) {
      const me = this.profile && this.profile.UserID;
      const a = this.msgAuthor(item);
      return me && a && a.UserID === me;
    },
    // ── Post overflow (⋯) menu ──────────────────────────────────────
    isActionMenuOpen(item) {
      return this.openActionMenuId === (item && item.TimelineID);
    },
    toggleActionMenu(item) {
      const id = item && item.TimelineID;
      this.openActionMenuId = this.openActionMenuId === id ? null : id;
    },
    closeActionMenu() {
      this.openActionMenuId = null;
    },
    // ── Post interactions (Like / Comment / Delete) ─────────────────
    isPostLiked(item) {
      const id = item && item.TimelineID;
      if (id && Object.prototype.hasOwnProperty.call(this.likeOverrides, id)) {
        return this.likeOverrides[id];
      }
      const me = this.profile && this.profile.UserID;
      const likedBy = (item && item.LikedBy) || [];
      return !!me && likedBy.some((u) => u === me || (u && u.UserID === me));
    },
    postLikeCount(item) {
      return parseInt(item && item.Likes) || 0;
    },
    isLikingPost(item) {
      return this.likingPostIds.indexOf(item && item.TimelineID) !== -1;
    },
    async togglePostLike(item) {
      const id = item && item.TimelineID;
      if (!id || this.likingPostIds.indexOf(id) !== -1) return;
      const wasLiked = this.isPostLiked(item);
      this.likingPostIds = [...this.likingPostIds, id];
      // Optimistic: flip the thumb + adjust the count immediately.
      this.$set(this.likeOverrides, id, !wasLiked);
      this.$store.commit('Timeline/setTimelineItemLikeState', {
        timelineId: id,
        liked: !wasLiked
      });
      try {
        if (wasLiked) await this.unlikePost(id);
        else await this.likePost(id);
      } catch (e) {
        // Revert on failure.
        this.$set(this.likeOverrides, id, wasLiked);
        this.$store.commit('Timeline/setTimelineItemLikeState', {
          timelineId: id,
          liked: wasLiked
        });
      } finally {
        this.likingPostIds = this.likingPostIds.filter((x) => x !== id);
      }
    },
    postComments(item) {
      return item && Array.isArray(item.Comments) ? item.Comments : [];
    },
    postCommentCount(item) {
      return this.postComments(item).length;
    },
    isCommentsOpen(item) {
      return this.openCommentPostIds.indexOf(item && item.TimelineID) !== -1;
    },
    toggleComments(item) {
      const id = item && item.TimelineID;
      if (!id) return;
      this.openCommentPostIds =
        this.openCommentPostIds.indexOf(id) !== -1
          ? this.openCommentPostIds.filter((x) => x !== id)
          : [...this.openCommentPostIds, id];
    },
    setCommentDraft(item, value) {
      const id = item && item.TimelineID;
      if (!id) return;
      this.$set(this.commentDrafts, id, value);
    },
    isCommentingPost(item) {
      return this.commentingPostIds.indexOf(item && item.TimelineID) !== -1;
    },
    async submitComment(item) {
      const id = item && item.TimelineID;
      if (!id) return;
      const text = (this.commentDrafts[id] || '').trim();
      if (!text || this.commentingPostIds.indexOf(id) !== -1) return;
      this.commentingPostIds = [...this.commentingPostIds, id];
      // Embed the author marker so the comment can show who wrote it
      // (the API returns Author: null on comments too).
      const aMarker = this.authorMarker();
      const message = aMarker ? `${aMarker}${text}` : text;
      try {
        await this.addComment({ timelineId: id, message, files: [] });
        this.$set(this.commentDrafts, id, '');
        this.$store.commit('Timeline/setPromise', null);
        await this.loadTimelineData();
      } catch (e) {
        this.$notify({
          group: 'default',
          type: 'error',
          title: this.$t('Views.LiveStream.couldNotPostComment'),
          text: this.$t('Views.LiveStream.tryAgainShortly')
        });
      } finally {
        this.commentingPostIds = this.commentingPostIds.filter((x) => x !== id);
      }
    },
    canDeletePost(item) {
      if (this.isOwnMessage(item)) return true;
      // The timeline API returns `Author: null` for many posts, so we
      // often can't prove ownership. When the author is unresolvable,
      // still surface Delete and let the backend enforce permission
      // (it rejects unauthorized deletes).
      const a = this.msgAuthor(item) || {};
      return !a.UserID;
    },
    isDeletingPost(item) {
      return this.deletingPostIds.indexOf(item && item.TimelineID) !== -1;
    },
    async deletePost(item) {
      const id = item && item.TimelineID;
      if (!id || this.deletingPostIds.indexOf(id) !== -1) return;
      if (!window.confirm(this.$t('Views.LiveStream.confirmDeletePost')))
        return;
      this.deletingPostIds = [...this.deletingPostIds, id];
      // Record + persist the deletion FIRST so the post stays hidden
      // across reloads and survives an in-flight poll — even if the
      // backend doesn't hard-delete it server-side.
      this.markPostDeleted(id);
      this.$store.commit('Timeline/removeTimelineItem', { itemId: id });
      try {
        await this.deleteItem(id);
      } catch (e) {
        // Best effort on the server; it stays hidden locally regardless.
      } finally {
        this.deletingPostIds = this.deletingPostIds.filter((x) => x !== id);
      }
    },
    // ── Persisted "deleted post" ids (per event) ───────────────────
    deletedPostsKey() {
      const eid = this.$store.getters['CurrentEvent/getId'] || 'event';
      return `tappin_deleted_posts_${eid}`;
    },
    loadDeletedPosts() {
      try {
        const raw = localStorage.getItem(this.deletedPostsKey());
        const arr = raw ? JSON.parse(raw) : [];
        if (Array.isArray(arr)) this.deletedPostIds = arr.map(String);
      } catch (e) {
        /* ignore corrupt storage */
      }
    },
    markPostDeleted(id) {
      const sid = String(id);
      if (this.deletedPostIds.indexOf(sid) === -1) {
        this.deletedPostIds = [...this.deletedPostIds, sid];
      }
      try {
        localStorage.setItem(
          this.deletedPostsKey(),
          JSON.stringify(this.deletedPostIds)
        );
      } catch (e) {
        /* ignore quota / disabled storage */
      }
    },
    canDeleteComment(comment) {
      const me = this.profile && this.profile.UserID;
      const a = this.msgAuthor(comment);
      return !!me && a && a.UserID === me;
    },
    async deleteCommentItem(item, comment) {
      const cid = comment && comment.TimelineID;
      const parentId = item && item.TimelineID;
      if (!cid) return;
      if (!window.confirm(this.$t('Views.LiveStream.confirmDeleteComment')))
        return;
      try {
        await this.deleteComment(cid);
        this.$store.commit('Timeline/removeTimelineComment', {
          parentId,
          TimelineId: cid
        });
      } catch (e) {
        this.$notify({
          group: 'default',
          type: 'error',
          title: this.$t('Views.LiveStream.couldNotDeleteComment'),
          text: this.$t('Views.LiveStream.tryAgainShortly')
        });
      }
    },
    cleanMessage(html) {
      if (!html) return '';
      // Strip the invisible session + author markers
      let cleaned = html
        .replace(/<!--tappin-session:[^>]*-->/g, '')
        .replace(/<!--tappin-author:[^>]*-->/g, '');
      // Sanitize: strip dangerous tags (script, iframe, object, embed,
      // form, input) and event handler attributes to prevent XSS from
      // user-generated timeline messages rendered via v-html.
      // <img> / <video> / <a class="ls-chat-file"> are kept so inline
      // post attachments (uploaded image / video / file chip) render.
      cleaned = cleaned
        .replace(
          /<\s*\/?\s*(script|iframe|object|embed|form|input|textarea|select|meta|link|style)\b[^>]*>/gi,
          ''
        )
        .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\s*on\w+\s*=\s*\S+/gi, '')
        .replace(/javascript\s*:/gi, '');
      return cleaned;
    },
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = this.cleanMessage(html);
      return (tmp.textContent || tmp.innerText || '').slice(0, 80);
    },
    setReplyTarget(item) {
      this.replyTarget = item;
      this.$nextTick(() => {
        const input = document.querySelector('.ls-chat-input__field');
        if (input) input.focus();
      });
    },
    startPrivateChat(item) {
      const author = item && item.UserID ? item : this.msgAuthor(item);
      if (!author || !author.UserID) return;
      this.openPrivateChatWith(author);
    },
    openPrivateChatWith(user) {
      if (!user || !user.UserID) return;
      // On mobile, open the chat as its own page so it gets a real
      // back button and isn't a tiny popup overlaying the participant
      // card. Desktop continues to use the modal experience.
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= 768;
      if (isMobile) {
        this.$router
          .push({
            name: 'private-chat',
            params: { userId: String(user.UserID) }
          })
          .catch(() => {
            /* duplicate-nav errors are harmless */
          });
        return;
      }
      // Desktop: open a docked, LinkedIn-style chat window (bottom-right)
      // instead of a full-screen modal so users can run several at once.
      this.$store.dispatch('Chat/openChatWindow', user);
    },
    triggerChatFilePicker() {
      const input = this.$refs.chatFileInput;
      if (input) input.click();
    },
    toggleEmojiPicker() {
      this.emojiPickerOpen = !this.emojiPickerOpen;
    },
    insertEmoji(emoji) {
      // Append to the end of the input. Preserving caret position
      // would need DOM-level work; appending is the predictable
      // behaviour most chat composers use.
      this.chatInput = (this.chatInput || '') + emoji;
      this.emojiPickerOpen = false;
      this.$nextTick(() => {
        const input = document.querySelector('.ls-chat-input__field');
        if (input) {
          input.focus();
          // Move caret to end so the next character types after the emoji.
          const len = input.value.length;
          try {
            input.setSelectionRange(len, len);
          } catch (e) {
            /* ignore */
          }
        }
      });
    },
    onDocClickEmoji(event) {
      if (!this.emojiPickerOpen) return;
      // Close when clicking outside the picker / its trigger.
      const wrap =
        event.target && event.target.closest
          ? event.target.closest('.ls-chat-input__field-wrap')
          : null;
      if (!wrap) this.emojiPickerOpen = false;
    },
    onChatFileSelected(event) {
      const file =
        event && event.target && event.target.files && event.target.files[0];
      // Reset the input so picking the same file twice still fires change.
      if (event && event.target) event.target.value = '';
      if (!file) return;
      // 4 MB cap — anything larger gets a confirmation prompt; the
      // current backend uses base64-embedded payloads so we keep
      // them small to avoid request bloat.
      const MAX_BYTES = 4 * 1024 * 1024;
      if (file.size > MAX_BYTES) {
        this.announceChatError(this.$t('Views.LiveStream.attachmentTooLarge'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const mime = (file.type || '').toLowerCase();
        let kind = 'file';
        if (mime.startsWith('image/')) kind = 'image';
        else if (mime.startsWith('video/')) kind = 'video';
        this.pendingAttachment = {
          name: file.name,
          size: file.size,
          dataUrl,
          kind
        };
      };
      reader.onerror = () => {
        this.announceChatError(this.$t('Views.LiveStream.couldNotReadFile'));
      };
      reader.readAsDataURL(file);
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    buildAttachmentHtml(att) {
      if (!att) return '';
      const safeName = this.escapeHtml(att.name);
      const safeSize = this.escapeHtml(this.formatFileSize(att.size));
      if (att.kind === 'image') {
        return `<img src="${att.dataUrl}" alt="${safeName}" class="ls-chat-img" />`;
      }
      if (att.kind === 'video') {
        return (
          `<video src="${att.dataUrl}" class="ls-chat-video" controls preload="metadata"></video>` +
          `<a href="${att.dataUrl}" download="${safeName}" class="ls-chat-file">` +
          `<span class="ls-chat-file-name">📎 ${safeName}</span>` +
          `<span class="ls-chat-file-size">${safeSize}</span>` +
          `</a>`
        );
      }
      return (
        `<a href="${att.dataUrl}" download="${safeName}" class="ls-chat-file">` +
        `<span class="ls-chat-file-name">📎 ${safeName}</span>` +
        `<span class="ls-chat-file-size">${safeSize}</span>` +
        `</a>`
      );
    },
    async sendChatMessage() {
      const raw = (this.chatInput || '').trim();
      const att = this.pendingAttachment;
      if (!raw && !att) return;
      if (this.sendingChat) return;
      this.sendingChat = true;

      // Prepend a quoted reply line so the recipient sees the context
      let msg = raw;
      if (this.replyTarget) {
        const replyName = this.msgAuthorName(this.replyTarget);
        const replyText = this.stripHtml(this.replyTarget.Message);
        msg = `<blockquote style="border-left:3px solid var(--color-primary, #047873);padding-left:8px;margin:0 0 6px;color:#6b7280;font-size:12px;">↩ ${replyName}: ${replyText}</blockquote>${raw}`;
      }

      // Append the attachment block (image preview / video player /
      // file download chip) so it renders inline alongside the text.
      if (att) {
        const attHtml = this.buildAttachmentHtml(att);
        msg = msg ? `${msg}<br />${attHtml}` : attHtml;
      }

      // Tag the message with the current session id so it only renders
      // in this specific session's chat — prevents cross-session leaks
      // when the Timeline API returns event-wide messages.
      const marker = this.sessionMarker();
      if (marker) msg = `${marker}${msg}`;
      // Embed the author (the timeline API returns Author: null), so the
      // post can show who wrote it. Stripped from display by cleanMessage.
      const aMarker = this.authorMarker();
      if (aMarker) msg = `${aMarker}${msg}`;

      try {
        await this.addTimelineEntry({ message: msg, files: [] });
        this.chatInput = '';
        this.replyTarget = null;
        this.pendingAttachment = null;
        // The Pusher 'timeline-add' event should refresh local state, but we
        // also force a reload here so the message appears immediately even
        // if Pusher is not connected on this page.
        this.$store.commit('Timeline/setPromise', null);
        await this.loadTimelineData();
        this.$nextTick(() => {
          const list = this.$refs.chatList;
          if (list) list.scrollTop = list.scrollHeight;
        });
      } catch (e) {
        console.error('[LiveStream] failed to send chat message', e);
        // Announce failure to screen readers via a live region
        this.announceChatError(this.$t('Views.LiveStream.sendFailed'));
      } finally {
        this.sendingChat = false;
      }
    },

    // ── Documents helpers ──────────────────────────────────────────────
    // Show all docs from the store if the platform-filtered list is empty
    docsToShow(filteredDocs) {
      if (filteredDocs && filteredDocs.length) return filteredDocs;
      return this.$store.getters['Documents/getItems'] || [];
    },
    docUrl(doc) {
      if (!doc) return '';
      // Different APIs use slightly different field names
      const raw =
        doc.Documentfile ||
        doc.DocumentFile ||
        doc.documentfile ||
        doc.url ||
        '';
      if (!raw) return '';
      // Already absolute? use as-is
      if (/^https?:\/\//i.test(raw)) return raw;
      // Otherwise resolve against the API base so relative paths returned
      // by the backend (e.g. "/api/asset/...") don't end up being opened
      // against the frontend host, which produces "site can't be reached".
      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '');
      const path = raw.startsWith('/') ? raw : `/${raw}`;
      return `${base}${path}`;
    },
    // Resolve the file extension from the document name first, then
    // fall back to the URL path — the backend's `Name` field often has
    // no extension, which is why type-based preview routing missed.
    docExtResolved(doc) {
      const name = (doc && doc.Name) || '';
      let m = name.match(/\.([a-z0-9]+)\s*$/i);
      if (m) return m[1].toLowerCase();
      const url = (this.docUrl(doc) || '').split(/[?#]/)[0];
      m = url.match(/\.([a-z0-9]+)$/i);
      return m ? m[1].toLowerCase() : '';
    },
    previewDocument(doc) {
      const url = this.docUrl(doc);
      if (!url) return;
      const ext = this.docExtResolved(doc);
      const officeExts = ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
      let viewUrl;
      if (officeExts.includes(ext)) {
        // Office formats can't render inline — Microsoft's viewer
        // fetches and renders them server-side.
        viewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
          url
        )}`;
      } else if (imageExts.includes(ext)) {
        // Images always preview inline.
        viewUrl = url;
      } else {
        // PDFs (and anything else) are routed through Google's viewer.
        // Opening the raw file often downloads it instead of previewing
        // — either because the browser can't render it, or because the
        // file is served with `Content-Disposition: attachment`. The
        // viewer renders it server-side, sidestepping both.
        viewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
          url
        )}`;
      }
      window.open(viewUrl, '_blank', 'noopener');
    },
    downloadDocument(doc) {
      const url = this.docUrl(doc);
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.Name || '';
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async emailAllDocuments() {
      // The backend only supports emailing the full document set
      // (there's no per-document email endpoint), so this sends every
      // session document to the signed-in attendee's inbox.
      if (this.isEmailingAllDocs) return;
      this.isEmailingAllDocs = true;
      try {
        await ApiClient.sendAllDocuments();
        this.$notify({
          group: 'default',
          type: 'success',
          duration: 5000,
          title: this.$t('Views.LiveStream.documentsSent'),
          text: this.$t('Views.LiveStream.documentsSentText')
        });
      } catch (e) {
        this.$notify({
          group: 'default',
          type: 'error',
          duration: 6000,
          title: this.$t('Views.LiveStream.couldNotEmailDocuments'),
          text: this.$t('Views.LiveStream.couldNotEmailDocumentsText')
        });
      } finally {
        this.isEmailingAllDocs = false;
      }
    },
    docExt(doc) {
      const name = doc && doc.Name;
      if (!name) return 'FILE';
      const m = name.match(/\.([^.]+)$/);
      return m ? m[1].toUpperCase() : 'FILE';
    },
    docIcon(doc) {
      const ext = this.docExt(doc).toLowerCase();
      if (ext === 'pdf') return 'fas fa-file-pdf';
      if (['ppt', 'pptx'].includes(ext)) return 'fas fa-file-powerpoint';
      if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word';
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fas fa-file-excel';
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext))
        return 'fas fa-file-image';
      if (['zip', 'rar', '7z'].includes(ext)) return 'fas fa-file-archive';
      return 'fas fa-file';
    },
    docIconClass(doc) {
      const ext = this.docExt(doc).toLowerCase();
      if (ext === 'pdf') return 'ls-doc-card__icon--red';
      if (['ppt', 'pptx'].includes(ext)) return 'ls-doc-card__icon--orange';
      if (['doc', 'docx'].includes(ext)) return 'ls-doc-card__icon--blue';
      if (['xls', 'xlsx', 'csv'].includes(ext))
        return 'ls-doc-card__icon--green';
      return 'ls-doc-card__icon--gray';
    },
    formatBytes(bytes) {
      if (!bytes) return '';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    },
    formatDocDate(date) {
      if (!date) return '';
      return Vue.moment(date).format('MMMM D, YYYY');
    },

    // ── Poll helpers ──────────────────────────────────────────────────
    // Convert a promoted timeline item into a card object. We try to
    // parse its Promolink first; if that fails or it isn't a known
    // poll type, we still show the item — the admin promoted it, so
    // it should appear in the Poll tab regardless.
    parsePromotedItem(item) {
      if (!item) return null;

      // Strip HTML for a clean title
      const tmp = document.createElement('div');
      tmp.innerHTML = item.Message || '';
      const text = (tmp.textContent || tmp.innerText || '').trim();
      const title =
        (item.Promolinktext && item.Promolinktext.trim()) ||
        text ||
        this.$t('Views.LiveStream.livePoll');

      // Try to parse the top-level Promolink first
      let parsed = null;
      const topLink = item.Promolink || item.promolink || item.link || '';
      if (topLink) {
        try {
          parsed = parsePromoLink(topLink);
        } catch (e) {
          parsed = null;
        }
      }

      // If there's no top-level Promolink, scan the message HTML for any
      // <a> tag whose href contains pg=poll/quiz/etc — that's how admins
      // embed poll links inside promoted messages.
      if (!parsed || !parsed.id) {
        const anchors = tmp.querySelectorAll('a[href]');
        for (const a of anchors) {
          const href = a.getAttribute('href') || '';
          // Match query strings like ?pg=poll&id=123 or pg=quiz&qid=456
          const qIndex = href.indexOf('?');
          const qs = qIndex >= 0 ? href.substring(qIndex + 1) : href;
          try {
            const candidate = parsePromoLink(qs);
            if (
              candidate &&
              candidate.page &&
              candidate.id &&
              [
                'poll',
                'quiz',
                'treasure_hunt',
                'coded_polls',
                'coded_quizzes',
                'quiz_x'
              ].includes(candidate.page)
            ) {
              parsed = candidate;
              break;
            }
          } catch (e) {
            /* ignore and try next */
          }
        }
      }

      // Only show the body if it has additional content beyond the title
      const showBody = text && text !== title && text.length > title.length;

      return {
        page: (parsed && parsed.page) || 'poll',
        id: (parsed && parsed.id) || item.TimelineID,
        title,
        timelineId: item.TimelineID,
        rawHtml: showBody ? item.Message || '' : '',
        hasLink: !!(parsed && parsed.id)
      };
    },
    hasAnyPromotedPoll(promotedItems) {
      return this.allPollItems(promotedItems).length > 0;
    },
    // Show ONLY real poll-style promotions in the Poll tab — items
    // that parse to a recognized poll page (poll / quiz / treasure
    // hunt / coded variants) AND carry a usable link/id. Plain-text
    // promoted messages do NOT belong here.
    allPollItems(promotedItems) {
      const POLL_PAGES = [
        'poll',
        'quiz',
        'quiz_x',
        'treasure_hunt',
        'coded_polls',
        'coded_quizzes'
      ];
      const fromSlot = promotedItems || [];
      const fromStore =
        this.$store.getters['Timeline/getPromotedTimelineItems'] || [];
      const items = fromStore.length ? fromStore : fromSlot;
      const polls = items
        .map((item) => this.parsePromotedItem(item))
        .filter((p) => p && p.hasLink && POLL_PAGES.includes(p.page));
      // De-dupe by timelineId
      const map = new Map();
      polls.forEach((p) => {
        const key = p.timelineId || `${p.page}-${p.id}`;
        if (!map.has(key)) map.set(key, p);
      });
      return Array.from(map.values());
    },
    promotedPolls(promotedItems) {
      return this.allPollItems(promotedItems);
    },
    pollVotesLabel(promotedPage, promotedItems) {
      const votes = (promotedPage && promotedPage.totalVotes) || 0;
      if (votes)
        return this.$tc('Views.LiveStream.totalVotes', votes, { n: votes });
      const polls = this.allPollItems(promotedItems);
      if (polls.length)
        return this.$tc('Views.LiveStream.pollsAvailable', polls.length, {
          n: polls.length
        });
      return this.$t('Views.LiveStream.waitingForNextPoll');
    },
    openPoll(poll) {
      // Only items with a real parsed Promolink can open the Poll
      // component (it needs an id + page that the PollDataProvider knows).
      if (poll && poll.hasLink) {
        this.$store.dispatch('Timeline/setPromotedPage', {
          id: poll.id,
          page: poll.page
        });
      }
    },
    pollTypeLabel(page) {
      const map = {
        poll: this.$t('Views.LiveStream.PollTypes.poll'),
        quiz: this.$t('Views.LiveStream.PollTypes.quiz'),
        quiz_x: this.$t('Views.LiveStream.PollTypes.quiz'),
        treasure_hunt: this.$t('Views.LiveStream.PollTypes.treasureHunt'),
        coded_polls: this.$t('Views.LiveStream.PollTypes.codePoll'),
        coded_quizzes: this.$t('Views.LiveStream.PollTypes.codeQuiz')
      };
      return map[page] || this.$t('Views.LiveStream.PollTypes.poll');
    }
  }
};
</script>

<style scoped lang="scss">
.live-stream-page {
  font-family: 'Poppins', sans-serif;
  // Never let the page scroll sideways: clip any horizontal overflow (long
  // labels, embedded iframes, the off-canvas drawer) to the viewport width so
  // there is no horizontal scrollbar on mobile.
  max-width: 100vw;
  overflow-x: hidden;

  // Global focus-visible indicator for keyboard users
  *:focus-visible {
    outline: 2px solid var(--color-primary, #047873);
    outline-offset: 2px;
  }
}

// Reset list styles for semantic ul used in people tab
ul.ls-people-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ls-wrap {
  // No max-width — the page now uses the full viewport width so
  // we don't waste big margins on wide monitors. Side padding
  // stays modest so the cards still have breathing room from the
  // edges.
  width: 100%;
  margin: 0 auto;
  padding: 20px 28px 40px;

  @media (max-width: 640px) {
    padding: 30px 14px 30px;
  }
}

.ls-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

// ── Responsive 2-column grid ──────────────────────────────────────────
// Stream on the LEFT, sidebar (icon rail + optional content) on the
// RIGHT. The icon rail sits on the LEFT side of that sidebar.
.ls-grid {
  display: grid;
  // Sidebar is wider now so the panel content has room to breathe;
  // gap between stream and sidebar is 28 px.
  grid-template-columns: minmax(0, 1fr) 440px;
  gap: 28px;
  // Anchor the entire grid to the viewport so neither column can
  // grow past the visible area and trigger a page scroll.
  height: calc(100vh - 140px);
  min-height: 480px;

  // Section is always shown at the same width — the icon rail is
  // now a drawer summoned from a small in-card hamburger button.
  &--rail-only {
    grid-template-columns: minmax(0, 1fr) 440px;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 20px;
    &--rail-only {
      grid-template-columns: 1fr;
    }
    // Mobile stacks vertically — let the page scroll naturally
    // since the left+right blocks won't fit in one viewport.
    height: auto;
    min-height: 0;
  }
}

// Mobile-only header slot (Program's greeting + My Programs pills,
// lifted out of the hero). Hidden on desktop so it doesn't occupy a
// grid cell; shown above everything on mobile.
.ls-mobile-top {
  display: none;
}

// Embedded mode — Program.vue mounts LiveStream as its shell with
// its hero/filters/sessions injected into the `main` slot. Program
// content is taller than one viewport and needs natural document
// scroll instead of the live-stream viewport-pinned layout.
.ls-grid--embedded {
  height: auto;
  min-height: 0;

  // Mobile: when the columns stack vertically, this gap is the only
  // space between the program content and the promoted card. A modest
  // 12px keeps them close without feeling cramped. Desktop untouched.
  // Also flip the stacking order so the mobile-top pills sit first,
  // then the promoted message, then the agenda/program content.
  @media (max-width: 1100px) {
    gap: 12px;

    > .ls-mobile-top {
      display: block;
      order: 0;
    }
    > .ls-side {
      order: 1;
    }
    > .ls-left {
      order: 2;
    }
  }

  // Side card scrolls with the page in embedded mode rather than
  // staying pinned — it sits at the top of the right column, flush
  // with the top of the left hero card, and moves together with the
  // program agenda as the page scrolls. Instead of being capped at
  // one viewport with an internal scroll, the card grows to fit its
  // content height — so the whole page scrolls as one, not the
  // section on its own.
  > .ls-side {
    height: auto;
    min-height: 0;
    align-self: start;

    // Let the promoted message + its body expand to their natural
    // height instead of clipping / scrolling inside the card.
    .ls-side__main,
    .ls-promoted {
      overflow: visible;
    }

    .ls-promoted {
      flex: 0 0 auto;
    }

    .ls-promoted__body {
      flex: 0 0 auto;
      overflow-y: visible;
    }

    // Mobile: the promoted card now sits ABOVE the agenda, so it's no
    // longer the page bottom and doesn't need App.vue's global
    // bottom-nav clearance (`padding-bottom: 80px !important`). That
    // clearance was leaving a big empty gap inside the card — cancel it.
    @media (max-width: 1100px) {
      padding-bottom: 0 !important;

      .ls-side__main {
        padding-bottom: 0 !important;
      }
    }

    // Mobile/tablet + a section open: the card was height:auto, so the
    // panel never became a bounded scroll area (and its
    // `overscroll-behavior: contain` swallowed touch scrolls). Bound the
    // card to the viewport — 64px navbar, 20px gap above, and 80px below
    // to clear the bottom-nav pill — so the panel scrolls inside it.
    @media (max-width: 1100px) {
      &.ls-side--detail {
        height: calc(100vh - 164px);
        height: calc(100dvh - 164px);
        min-height: 320px;
        padding-bottom: 0 !important;

        .ls-side__main {
          overflow: hidden;
          min-height: 0;
          padding-bottom: 0 !important;
        }

        .ls-panel--scroll {
          min-height: 0;
        }
      }
    }

    // Desktop + a section open (Information, Sessions, …): pin the card
    // to the viewport below the sticky 64px navbar with an equal 20px gap
    // above and below, so the panel scrolls INSIDE the card instead of
    // the card growing with its content.
    @media (min-width: 1101px) {
      &.ls-side--detail {
        position: sticky;
        top: 84px; // 64px navbar + 20px gap
        height: calc(100vh - 104px); // 64px navbar + 20px gap top & bottom
        min-height: 0;

        .ls-side__main {
          overflow: hidden;
        }
      }
    }
  }
}

// ── LEFT side: header + player ────────────────────────────────────────
.ls-left {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  overflow: hidden;

  // Embedded slot content owns its own scrolling / layout — remove
  // the hard overflow clip and the fixed-height assumptions.
  &--embedded {
    overflow: visible;
    min-height: 0;
    height: auto;
  }

  // Desktop with a session description: the column is pinned to the
  // viewport, so the player (flex-basis 0) collapsed to nothing and the
  // description was clipped. Keep the player at least 16:9 and let the
  // column scroll instead. Mobile already stacks at natural height.
  &--has-about {
    @media (min-width: 1101px) {
      overflow-y: auto;

      > .ls-player {
        flex: 1 0 auto;
        aspect-ratio: 16 / 9;
      }

      > .ls-about {
        flex-shrink: 0;
      }
    }
  }
}

.ls-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  &__back {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #374151;
    font-size: 16px;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__title-block {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 640px) {
      font-size: 16px;
    }
  }

  // Inline meta row — speaker name + bullet separator + start/end
  // time, all on the SAME line. Wraps on narrow viewports so the
  // header stays readable instead of one of them being clipped.
  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 4px 0 0;
    line-height: 1.3;
  }

  &__speaker {
    font-size: 14px;
    color: var(--color-primary, #047873);
    font-weight: 500;
  }

  &__sep {
    color: #d1d5db;
    font-size: 12px;
    line-height: 1;
  }

  &__time {
    font-size: 12px;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 11px;
      color: #6b7280;
    }
  }
}

.ls-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
    animation: ls-pulse 1.6s ease-in-out infinite;
  }

  // Desktop-only variant: yields the slot to the mobile
  // Show/Hide stream toggle below the breakpoint.
  &--desktop {
    @media (max-width: 1100px) {
      display: none;
    }
  }
}

.ls-stream-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  flex-shrink: 0;

  i {
    font-size: 12px;
    color: var(--color-primary, #047873);
  }

  &:hover,
  &:focus-visible {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #111827;
    outline: none;
  }

  &[aria-pressed='true'] {
    background: rgba(4, 120, 115, 0.08);
    border-color: rgba(4, 120, 115, 0.25);
    color: var(--color-primary, #047873);

    i {
      color: var(--color-primary, #047873);
    }
  }

  // Mobile-only variant: takes the Live badge's slot in the
  // header on small viewports.
  &--mobile {
    display: none;

    @media (max-width: 1100px) {
      display: inline-flex;
    }
  }
}

// ── About this session — sits directly below the player.
//   Mirrors the Sponsor page's "About" block visually so the
//   live-stream surface stays consistent with the rest of the
//   platform.
.ls-about {
  margin-top: 4px;
  padding: 18px 20px 22px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: 'Poppins', sans-serif;

  // Mobile: when the user collapses the stream via the Hide-stream
  // toggle, also collapse the About block — it's tied to the
  // session video and shouldn't keep occupying space below an
  // already-hidden player on small screens.
  &--mobile-hidden {
    @media (max-width: 1100px) {
      display: none;
    }
  }

  &__title {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
  }

  &__body {
    color: #374151;
    font-size: 14px;
    line-height: 1.6;

    ::v-deep p {
      margin: 0 0 10px;
    }
    ::v-deep p:last-child {
      margin-bottom: 0;
    }
    ::v-deep a {
      color: var(--color-primary, #047873);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    ::v-deep ul,
    ::v-deep ol {
      margin: 0 0 10px;
      padding-left: 22px;
    }
    ::v-deep li {
      margin: 4px 0;
    }
    ::v-deep img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }
  }
}

// ── Player ────────────────────────────────────────────────────────────
.ls-player {
  position: relative;
  width: 100%;
  // Desktop: fills the remaining vertical space inside `.ls-left`
  // (which is height-bounded by the grid) so the page itself never
  // scrolls. The embedded stream / iframe handles its own internal
  // aspect ratio inside this container.
  flex: 1 1 0;
  min-height: 0;
  background: #0a0a0a;
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 1100px) {
    // Mobile/tablet stacks vertically — revert to natural 16:9.
    flex: 0 0 auto;
    aspect-ratio: 16 / 9;
  }

  &__video {
    position: absolute;
    inset: 0;
    color: #ffffff;
    overflow: hidden;
  }

  &__stream {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;

    ::v-deep .screen-height,
    ::v-deep .video-container,
    ::v-deep .h-full {
      height: 100% !important;
      width: 100% !important;
    }

    ::v-deep iframe,
    ::v-deep video {
      width: 100% !important;
      height: 100% !important;
      border: 0;
      display: block;
    }
  }

  &__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #ffffff;
  }

  &__eye {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
    font-size: 24px;
    backdrop-filter: blur(6px);
  }

  &__active-text {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  &__overlay-stats {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }
  &__overlay-active {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    border-radius: 9999px;
    background: rgba(0, 0, 0, 0.55);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    backdrop-filter: blur(6px);

    i {
      font-size: 11px;
    }
  }

  &__active-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #16a34a;
    box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.35);
  }

  &__controls-left {
    position: absolute;
    bottom: 16px;
    left: 16px;
    display: flex;
    gap: 8px;
    z-index: 2;
  }
  &__controls-right {
    position: absolute;
    bottom: 16px;
    right: 16px;
    z-index: 2;
  }

  &__ctrl {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: none;
    background: rgba(0, 0, 0, 0.55);
    color: #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(6px);
    transition: background 0.15s;
    font-size: 13px;

    &:hover {
      background: rgba(0, 0, 0, 0.75);
    }
  }
}

// ── RIGHT side: tabs ──────────────────────────────────────────────────
.ls-side {
  // Single-card side pane.
  //   • Desktop (>1100px): icon rail sits on the RIGHT, panel
  //     content fills the rest. NOT overflow-hidden so the rail's
  //     hover tooltip can spill outside the card.
  //   • Mobile  (≤1100px): icon rail becomes an absolute drawer
  //     summoned by the `.ls-menu-toggle` hamburger; card stacks
  //     vertically and clips the drawer to the rounded corners.
  position: relative;
  // Confine all internal z-indexes (hamburger toggle, drawer,
  // backdrop, hover tooltips) to the side card's own stacking
  // context so they can't poke through global overlays like the
  // Navbar's mobile menu.
  isolation: isolate;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: row;
  // Anchor the card to the viewport so the page itself never
  // scrolls. Long panel content (chat list, sponsors list, etc.)
  // scrolls INSIDE its own region instead of growing the card.
  height: calc(100vh - 140px);
  min-height: 480px;

  // DOM has the panel column first, then the rail. Visual order:
  // panel content sits on the LEFT, icon rail sits on the RIGHT
  // edge of the card.
  > .ls-side__main {
    order: 0;
  }
  > .ls-icon-bar {
    order: 1;
  }

  &__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden; // ← keeps the page from growing; panels
    //   handle their own internal scroll
    background: #ffffff;
    // Thin separator between the panel content and the rail (which
    // now sits on the RIGHT of the card). Panels own their own
    // padding so the line sits flush against the rail.
    border-right: 1px solid #f1f2f4;
  }

  // No-op modifier — kept so upstream JS toggling it doesn't break.
  &--rail-only &__main {
    display: flex;
  }

  @media (max-width: 1100px) {
    flex-direction: column;
    // Clip the drawer to the rounded corners when it slides in.
    overflow: hidden;

    &__main {
      // Mobile drawer flow — no rail occupies space inline, so the
      // desktop separator would draw a stray vertical line.
      border-right: none;
    }
  }

  @media (max-width: 640px) {
    border-radius: 12px;
  }
}

// ── Hamburger button — mobile-only in-card toggle that summons
//    the icon rail drawer. Sits absolutely in the top-RIGHT of the
//    side card. Hidden on desktop where the rail is permanently
//    visible as the left column.
.ls-menu-toggle {
  display: none; // desktop: rail is permanent, no toggle needed

  @media (max-width: 1100px) {
    position: absolute;
    top: 24px;
    right: 22px;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: #374151;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    z-index: 30; // above promoted message + panel content
    transition: color 0.15s, transform 0.15s;

    &:hover,
    &:focus-visible {
      color: var(--color-primary, #047873);
      outline: none;
      transform: translateY(-1px);
    }

    &.ls-menu-toggle--open {
      color: #001838;

      &:hover,
      &:focus-visible {
        color: #001838;
      }
    }
  }

  @media (max-width: 640px) {
    width: 34px;
    height: 34px;
    top: 20px;
    right: 18px;
    font-size: 16px;
  }
}

// ── Drawer backdrop — semi-transparent overlay confined to the side
//    card. Tapping it closes the drawer. Mobile-only: the rail is
//    permanently visible on desktop so a backdrop would be wrong.
.ls-drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 25;
  cursor: pointer;

  @media (min-width: 1101px) {
    display: none;
  }
}

// Vue transition classes for the backdrop fade. The rail slide-in
// is handled with pure CSS via `.ls-side--drawer-open .ls-icon-bar`
// so it can stay always-rendered (and always-visible on desktop).
.ls-drawer-enter-active,
.ls-drawer-leave-active {
  transition: opacity 0.2s ease;
}
.ls-drawer-enter,
.ls-drawer-leave-to {
  opacity: 0;
}

// ── Promoted message panel (admin-pinned poll / post) ────────────
//
// The message body is fully customizable from the admin side, so
// the wrapper has to bring its own visual personality without
// fighting whatever HTML the admin has dropped in. Approach:
//   • soft tinted gradient background (primary → white)
//   • header strip with an animated dot + brand pill
.ls-promoted {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    // Desktop: no hamburger overhead, normal symmetric padding.
    // Mobile: extra right padding (64px) reserves room for the
    // hamburger toggle that floats over this corner.
    padding: 24px 22px 6px 22px;
    flex-shrink: 0;
    // Match the menu button's height so the (smaller) pill stays
    // centered on the same baseline as the button.
    min-height: 38px;

    @media (max-width: 1100px) {
      padding-right: 64px;
    }
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 9999px;
    // Button fill tokens (not the contrast-darkened accent) so the pill
    // matches the event's buttons, as it already does in dark mode.
    background: var(--t-btn, #047873);
    color: var(--t-btn-on, #ffffff);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    box-shadow: 0 3px 8px rgba(4, 120, 115, 0.22);

    i {
      font-size: 18px;
    }
  }

  // Pulsing dot inside the pill — small live-feel cue that the
  // message is freshly promoted by the host. Clean solid disc, no
  // soft halo around it.
  &__pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ffffff;
    animation: ls-pulse 1.6s ease-in-out infinite;
    flex-shrink: 0;
  }

  &__body {
    // Content-sized when it fits, scrollable when it doesn't.
    // `flex: 0 1 auto` means the body never grows beyond its content
    // (so a short message stays compact) but CAN shrink inside the
    // parent's `overflow: hidden` column — paired with
    // `overflow-y: auto` that produces a usable scroll region when
    // the promoted message is longer than the available height.
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 14px 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  // Back button (icon-only). Used inside the panel-style header
  // when a poll is active in place of the promoted pill.
  &__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #374151;
    font-size: 13px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s, transform 0.15s;

    i {
      font-size: 13px;
    }

    &:hover,
    &:focus-visible {
      background: #f3f4f6;
      color: var(--color-primary, #047873);
      outline: none;
      transform: translateX(-1px);
    }
  }

  // Panel-style header matching the Sessions / Speakers / Sponsors
  // / Information / Posts visual pattern (white strip, light border,
  // 15px dark title, primary-teal subtitle).
  &__panel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 18px 12px;
    background: #ffffff;
    border-bottom: 1px solid #f3f4f6;
    flex-shrink: 0;

    @media (max-width: 1100px) {
      padding-right: 64px; // clear the floating hamburger
    }
  }

  &__panel-titles {
    flex: 1;
    min-width: 0;
  }

  &__panel-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.25;
  }

  &__panel-sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    font-weight: 500;
    margin: 4px 0 0;
  }

  // Container for the inline Poll component while a poll button
  // from the promoted message has been clicked. We re-style the
  // legacy Poll/SinglePoll/QuizPoll markup (which was authored for
  // the Stage view's Tailwind utility-class look) so it blends
  // with the rest of the platform design.
  &__poll {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    font-family: 'Poppins', sans-serif;

    // Drop the legacy `.p-4` wrapper's padding — the panel's own
    // `__body` padding already gives the content breathing room
    // and we don't want a second nested gutter.
    ::v-deep .p-4 {
      padding: 0;
    }

    // Headings (e.g. "Thanks for your answer", question titles).
    ::v-deep h3 {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      margin: 0 0 10px;
      line-height: 1.3;
    }

    // The poll question / description body text.
    ::v-deep .text-sm,
    ::v-deep p {
      font-size: 13px;
      line-height: 1.5;
      color: #374151;
    }

    // Answer labels — soft gray pills, no border, sized so 4-5
    // options fit comfortably without forcing a scroll.
    ::v-deep label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      border: none;
      border-radius: 9999px;
      margin-bottom: 8px;
      cursor: pointer;
      font-size: 13px;
      color: #374151;
      background: #f3f4f6;
      transition: background 0.12s, color 0.12s;

      &:hover {
        background: #e5e7eb;
        color: #111827;
      }

      input[type='radio'],
      input[type='checkbox'] {
        margin: 0;
        accent-color: var(--color-primary, #047873);
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    // Free-text textarea / input fields inside the poll.
    ::v-deep textarea,
    ::v-deep input[type='text'] {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      font-family: inherit;
      font-size: 13px;
      color: #111827;
      transition: border-color 0.12s, box-shadow 0.12s;

      &:focus {
        outline: none;
        border-color: var(--color-primary, #047873);
        box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.12);
      }
    }

    // The legacy "button button-primary" classes used for
    // submit / close — match the platform's primary CTA style.
    ::v-deep .button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 7px 16px;
      border-radius: 9999px;
      border: none;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
    }

    ::v-deep .button-primary {
      background: var(--color-primary, #047873);
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(4, 120, 115, 0.22);

      &:hover,
      &:focus-visible {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(4, 120, 115, 0.3);
        outline: none;
      }

      &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 2px 6px rgba(4, 120, 115, 0.15);
      }
    }

    ::v-deep .button-secondary {
      background: #f3f4f6;
      color: #374151;

      &:hover,
      &:focus-visible {
        background: #e5e7eb;
        color: #111827;
        outline: none;
      }
    }

    // PollActions row — Close on the far LEFT, Send on the far
    // RIGHT, with the gap between them filled with auto space.
    ::v-deep .poll-actions,
    ::v-deep .actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-top: 14px;
    }

    // Many of Poll.vue's inner blocks use Tailwind's `mt-2`/`mb-2`
    // utilities which translate to 8 px margins. With the panel now
    // tightened up, halve those so spacing stays consistent.
    ::v-deep .mt-2 {
      margin-top: 6px !important;
    }
    ::v-deep .mb-2 {
      margin-bottom: 6px !important;
    }
    ::v-deep .my-2 {
      margin-top: 6px !important;
      margin-bottom: 6px !important;
    }
    ::v-deep .mt-4 {
      margin-top: 10px !important;
    }
    ::v-deep .mb-4 {
      margin-bottom: 10px !important;
    }
  }

  // ── "You've already answered" panel — shown in place of the live
  //    Poll form when the local user has previously submitted this
  //    poll (tracked via localStorage / API isAnswered flag).
  &__answered {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 28px 22px 26px;
    gap: 10px;
  }

  &__answered-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(4, 120, 115, 0.12);
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 4px;
  }

  &__answered-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__answered-copy {
    font-size: 13px;
    line-height: 1.55;
    color: #6b7280;
    margin: 0;
    max-width: 280px;
  }

  &__answered-cta {
    margin-top: 8px;
    padding: 9px 22px;
    border-radius: 9999px;
    border: none;
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 12px rgba(4, 120, 115, 0.22);

    &:hover,
    &:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(4, 120, 115, 0.3);
      outline: none;
    }
  }
}

// ── Empty state shown when nothing is active and no promoted
//    message is pinned. Nudges the user toward the hamburger menu.
.ls-side-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 28px;
  gap: 14px;

  &__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(4, 120, 115, 0.08);
    color: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__copy {
    font-size: 13px;
    line-height: 1.55;
    color: #6b7280;
    margin: 0;
    max-width: 260px;
  }

  &__cta {
    margin-top: 6px;
    padding: 9px 22px;
    border-radius: 9999px;
    border: none;
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 4px 12px rgba(4, 120, 115, 0.25);

    &:hover,
    &:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(4, 120, 115, 0.32);
      outline: none;
    }

    // The "Open menu" CTA only makes sense on mobile, where the
    // rail is hidden behind the hamburger. On desktop the rail is
    // permanently visible so the button is redundant.
    &--mobile {
      display: none;

      @media (max-width: 1100px) {
        display: inline-flex;
      }
    }
  }
}

// ── Icon rail — dual-mode.
//   • Desktop (>1100px): permanent right column inside the side
//     card. Transparent, icons stacked from the TOP, hover reveals
//     a midnight pill tooltip with the section name.
//   • Mobile (≤1100px): hidden by default; slides in from the
//     RIGHT of the side card as a labeled drawer when the parent
//     gets `.ls-side--drawer-open` (toggled by the hamburger).
.ls-icon-bar {
  // === DESKTOP DEFAULTS ===
  flex-shrink: 0;
  width: 64px;
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; // icons stacked from the TOP
  gap: 8px;
  padding: 16px 0;
  // IMPORTANT: must stay `visible` on the desktop rail. As soon as
  // any axis is set to a non-visible value (auto/scroll/hidden),
  // browsers silently clip the other axis too — which would hide
  // the hover tooltip pill that sits 10 px to the LEFT of each
  // icon. The desktop rail has at most ~8 buttons centered, so
  // it's never going to overflow vertically anyway.
  overflow: visible;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &__btn {
    position: relative;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: transparent;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 0;
    font-size: 16px;
    font-family: 'Poppins', sans-serif;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s, transform 0.15s;

    i {
      font-size: 16px;
      color: inherit;
      transition: color 0.15s;
    }

    &:hover,
    &:focus-visible {
      background: rgba(0, 24, 56, 0.08);
      color: #001838;
      outline: none;
      // Rail sits on the RIGHT of the card now — nudge icons LEFT
      // on hover so they move toward the panel content.
      transform: translateX(-2px);
    }

    &--active {
      background: var(--color-primary, #047873);
      color: #ffffff;

      &:hover {
        background: var(--color-primary, #047873);
        color: #ffffff;
        transform: none;
      }
    }
  }

  // Desktop label = midnight pill tooltip floating to the LEFT of
  // the icon — rail now sits on the RIGHT edge of the side card so
  // the tooltip points inward across the panel content rather than
  // off the right side of the page. Hidden until hover.
  &__label {
    position: absolute;
    right: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%) translateX(4px);
    background: #001838;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 9999px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s, transform 0.15s;
    z-index: 20;
    font-family: 'Poppins', sans-serif;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    // Caret on the RIGHT edge of the pill, pointing back at the
    // icon to its right.
    &::before {
      content: '';
      position: absolute;
      right: -4px;
      top: 50%;
      transform: translateY(-50%) rotate(45deg);
      width: 8px;
      height: 8px;
      background: #001838;
    }
  }

  &__btn:hover &__label,
  &__btn:focus-visible &__label {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }

  // === MOBILE DRAWER MODE (≤1100px) ===
  @media (max-width: 1100px) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 220px;
    background: #ffffff;
    border: none;
    border-left: 1px solid #e5e7eb;
    box-shadow: -6px 0 18px rgba(15, 23, 42, 0.08);
    align-items: stretch;
    justify-content: flex-start;
    gap: 4px;
    padding: 72px 12px 16px; // clear the floating hamburger
    z-index: 26; // above backdrop (25), below toggle (30)
    overflow-x: hidden;
    overflow-y: auto; // many menu items: scroll inside the drawer
    // Hidden by default; slides in when the parent flips state.
    transform: translateX(100%);
    visibility: hidden;
    transition: transform 0.25s ease, visibility 0.25s;

    &__btn {
      width: 100%;
      height: 42px;
      border-radius: 10px;
      justify-content: flex-start;
      gap: 12px;
      padding: 0 14px;
      font-size: 14px;
      font-weight: 500;

      i {
        width: 18px;
        font-size: 15px;
        text-align: center;
        color: #6b7280;
      }

      &:hover,
      &:focus-visible {
        background: rgba(0, 24, 56, 0.06);
        color: #001838;
        transform: none;

        i {
          color: #001838;
        }
      }

      &--active {
        i {
          color: #ffffff;
        }

        &:hover i {
          color: #ffffff;
        }
      }
    }

    // Inline label on mobile — overrides the desktop tooltip pill.
    &__label {
      position: static;
      transform: none;
      background: transparent;
      color: inherit;
      font-size: 14px;
      font-weight: 500;
      padding: 0;
      border-radius: 0;
      box-shadow: none;
      pointer-events: auto;
      opacity: 1;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &::before {
        display: none;
      }
    }
  }

  @media (max-width: 640px) {
    width: 78%;
    max-width: 260px;
  }
}

// Mobile: Posts lives in the bottom nav, so drop it from the hamburger
// drawer. Desktop keeps it in the icon rail.
.ls-icon-bar__btn--posts {
  @media (max-width: 1100px) {
    display: none;
  }
}

// Drawer slide-in trigger on mobile when the side card is flagged
// as open by the hamburger.
.ls-side--drawer-open .ls-icon-bar {
  @media (max-width: 1100px) {
    transform: translateX(0);
    visibility: visible;
  }
}

// ── Master menu (vertical list of section names) ─────────────────
// Visible only when no section is open. Each row is a button that
// drills into the corresponding detail panel.
.ls-menu {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  background: #ffffff;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    border: none;
    background: transparent;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    text-align: left;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;
    transition: background 0.12s, color 0.12s;

    &:hover,
    &:focus-visible {
      background: #f9fafb;
      color: var(--color-primary, #047873);
      outline: none;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #f3f4f6;
    color: var(--color-primary, #047873);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 14px;
    transition: background 0.12s, color 0.12s;
  }

  &__row:hover &__icon,
  &__row:focus-visible &__icon {
    background: #e6f5f4;
  }

  &__label {
    flex: 1;
    min-width: 0;
  }

  &__chevron {
    color: #9ca3af;
    font-size: 12px;
    flex-shrink: 0;
  }
}

// ── Shared section-header layout — every detail panel header now
//    opens with an icon-only back arrow inline with its title.
.ls-section-header {
  // Mobile: clear the floating hamburger button (top-right corner)
  // so the title block isn't obscured. !important keeps section-
  // specific padding rules from overriding it later in the cascade.
  @media (max-width: 1100px) {
    padding-right: 64px !important;
  }
  @media (max-width: 640px) {
    padding-right: 54px !important;
  }

  // Row that holds the back button + title block. Sections with
  // extra controls (search box, category filter) place them BELOW
  // this row inside the same header element.
  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__titles {
    flex: 1;
    min-width: 0;
  }
}

// Icon-only back arrow used at the start of every section header.
.ls-section-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, transform 0.15s;

  i {
    font-size: 13px;
  }

  &:hover,
  &:focus-visible {
    background: #f3f4f6;
    color: var(--color-primary, #047873);
    outline: none;
    transform: translateX(-1px);
  }
}

// Chat panel header — Posts title + message count. Matches the
// Sessions/Speakers/Sponsors/Information visual pattern.
.ls-chat-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    font-weight: 500;
    margin: 4px 0 0;
  }
}

.ls-panel {
  flex: 1;
  min-width: 0; // allow panel to shrink inside the horizontal row
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &--chat {
    display: flex;
    flex-direction: column;
  }

  // Generic scrollable panel — used by Information / Sessions /
  // Speakers / Sponsors which render long lists.
  &--scroll {
    overflow-y: auto;
  }

  // Interaction panel — embeds an admin-configured webpage in an iframe
  // that should fill the available height inside the side card.
  &--interaction {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    // Allow vertical scroll so the iframe can use its full min-height
    // even when the side card is shorter than the embedded page.
    overflow-y: auto;
  }
}

// ── Interaction (custom webpage) panel ────────────────────────────────
.ls-interaction-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  width: 100%;
  background-color: #ffffff;
  padding: 12px 16px 16px;
  // Ensure the body itself takes the full remaining height of the
  // side card so the iframe inside can stretch — without this the
  // iframe collapses to its min-height on shorter viewports.
  height: 100%;
}

.ls-interaction-iframe {
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  // Give the embedded webpage plenty of room — tall enough that
  // sites like Kahoot show their full Game-PIN / Enter UI without
  // an inner scrollbar.
  min-height: 900px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background-color: #ffffff;
}

// ── Chat panel ────────────────────────────────────────────────────────
.ls-chat-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ls-chat-empty {
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  padding: 40px 16px;
}

.ls-chat-msg {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  &__avatar {
    width: 36px !important;
    height: 36px !important;
    flex-shrink: 0;
    ::v-deep img {
      width: 100% !important;
      height: 100% !important;
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
  }

  &__time {
    font-size: 11px;
    color: #6b7280;
  }

  &__text {
    font-size: 13px;
    color: #374151;
    background: #f3f4f6;
    border-radius: 12px;
    padding: 10px 12px;
    line-height: 1.45;
    word-wrap: break-word;
  }

  &__reply {
    border-left: 3px solid var(--color-primary, #047873);
    padding: 4px 8px;
    margin-bottom: 4px;
    background: #f9fafb;
    border-radius: 0 6px 6px 0;
    font-size: 11px;
    color: #6b7280;
  }

  &__reply-name {
    font-weight: 700;
    margin-right: 6px;
    color: var(--color-primary, #047873);
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
    opacity: 1;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: transparent;
    border: none;
    color: #6b7280;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
    font-family: 'Poppins', sans-serif;

    i {
      font-size: 9px;
    }

    &:hover {
      background: #f3f4f6;
      color: var(--color-primary, #047873);
    }

    &--active {
      color: var(--color-primary, #047873);
      font-weight: 700;
    }

    &--danger:hover {
      background: #fef2f2;
      color: #dc2626;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  // ── Overflow (⋯) menu ─────────────────────────────────────────
  &__more {
    position: relative;
    display: inline-flex;
  }

  // The ⋯ trigger is always visible (not hover-only).
  &__more-btn {
    transition: background 0.15s, color 0.15s;

    i {
      font-size: 12px;
    }

    &--open {
      background: #f3f4f6;
      color: var(--color-primary, #047873);
    }
  }

  &__menu {
    position: absolute;
    top: 100%;
    right: 0;
    left: auto;
    margin-top: 4px;
    min-width: 168px;
    max-width: calc(100vw - 32px);
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    padding: 6px;
    z-index: 30;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: transparent;
    border: none;
    border-radius: 7px;
    cursor: pointer;
    font-size: 13px;
    color: #374151;
    text-align: left;
    font-family: 'Poppins', sans-serif;

    i {
      width: 14px;
      font-size: 12px;
      color: #6b7280;
    }

    &:hover {
      background: #f3f4f6;
    }

    &--danger {
      color: #dc2626;

      i {
        color: #dc2626;
      }

      &:hover {
        background: #fef2f2;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  // ── Comments thread under a post ──────────────────────────────
  &__body .ls-chat-comments {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f1f2f4;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.ls-chat-comment {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &__avatar {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__bubble {
    flex: 1;
    min-width: 0;
    background: #f3f4f6;
    border-radius: 10px;
    padding: 6px 10px;
  }

  &__name {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: #111827;
  }

  &__text {
    font-size: 12px;
    color: #374151;
    line-height: 1.4;
    word-break: break-word;
  }

  &__delete {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 50%;
    font-size: 10px;

    &:hover {
      background: #fef2f2;
      color: #dc2626;
    }
  }
}

.ls-chat-comment-input {
  display: flex;
  align-items: center;
  gap: 6px;

  &__field {
    flex: 1;
    min-width: 0;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    padding: 6px 12px;
    font-size: 12px;
    font-family: 'Poppins', sans-serif;
    outline: none;

    &:focus {
      border-color: var(--color-primary, #047873);
    }
  }

  &__send {
    flex-shrink: 0;
    border: none;
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 9999px;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

.ls-chat-reply-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;

  &__body {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;

    > div {
      flex: 1;
      min-width: 0;
    }
  }

  &__name {
    font-size: 12px;
    font-weight: 700;
    color: var(--color-primary, #047873);
    margin-bottom: 2px;
  }

  &__text {
    font-size: 11px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__close {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;

    &:hover {
      background: #e5e7eb;
      color: #111827;
    }
  }
}

.ls-chat-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  &__attach,
  &__send {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 14px;
    transition: background 0.15s;
  }

  &__attach {
    background: transparent;
    color: #6b7280;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }

  &__file {
    display: none;
  }

  &__send {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover:not(:disabled) {
      background: var(--color-primary-hover, #036663);
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  // Wrapper around the text input so the emoji button can sit
  // inside it on the right edge, with the picker dropdown anchored
  // to the wrapper.
  &__field-wrap {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  &__field {
    flex: 1 1 0;
    min-width: 0;
    padding: 9px 38px 9px 14px; // right padding clears emoji button
    background: #f3f4f6;
    border: 1.5px solid #b0b8c4;
    border-radius: 10px;
    font-size: 13px;
    outline: none;
    font-family: 'Poppins', sans-serif;

    &::placeholder {
      color: #6b7280;
    }
    &:focus {
      border-color: var(--color-primary, #047873);
      background: #ffffff;
    }
  }

  &__emoji {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.15s, color 0.15s;

    &:hover,
    &:focus-visible {
      background: #e5e7eb;
      color: #111827;
      outline: none;
    }
  }
}

// ── Emoji picker popover ────────────────────────────────────────
.ls-emoji-picker {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: 240px;
  max-height: 220px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 20px -4px rgb(0 0 0 / 0.12),
    0 4px 8px -4px rgb(0 0 0 / 0.08);
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;

  &__btn {
    width: 100%;
    aspect-ratio: 1;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    padding: 0;
    transition: background 0.1s;

    &:hover,
    &:focus-visible {
      background: #f3f4f6;
      outline: none;
    }
  }
}

.ls-emoji-fade-enter-active,
.ls-emoji-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.ls-emoji-fade-enter,
.ls-emoji-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

// ── Pending-attachment preview (above the chat input) ──────────────
.ls-chat-attach-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 14px 0;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #e5e7eb;
    color: #374151;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--image {
      background: #e6f5f4;
    }
    &--video {
      background: #ede9fe;
      color: #6d28d9;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__size {
    font-size: 11px;
    color: #6b7280;
  }

  &__remove {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: #e5e7eb;
      color: #111827;
    }
  }
}

// ── Inline attachment styles inside rendered messages ──────────────
::v-deep .ls-chat-img {
  max-width: 100%;
  max-height: 240px;
  border-radius: 10px;
  margin-top: 4px;
  display: block;
}

::v-deep .ls-chat-video {
  max-width: 100%;
  max-height: 280px;
  border-radius: 10px;
  margin-top: 4px;
  display: block;
  background: #000;
}

::v-deep .ls-chat-file {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-top: 6px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 12px;
  color: #111827;
  text-decoration: none;

  &:hover {
    background: #e5e7eb;
  }

  .ls-chat-file-name {
    font-weight: 600;
  }
  .ls-chat-file-size {
    color: #6b7280;
  }
}

// ── Docs panel ────────────────────────────────────────────────────────
.ls-docs-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;

  // Let the "Send all documents" pill drop to its own line when a longer
  // translation doesn't fit beside the title, instead of overlapping it.
  .ls-section-header__row {
    flex-wrap: wrap;
  }

  .ls-section-header__titles {
    min-width: 150px;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    font-weight: 500;
    margin: 4px 0 0;
  }
}

// "Email all" action in the documents header — soft pill matching the
// other section header controls.
.ls-docs-mailall {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  color: var(--color-primary, #047873);
  background: rgba(4, 120, 115, 0.08);
  border: none;
  padding: 6px 12px;
  border-radius: 9999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, opacity 0.15s;

  &:hover:not(:disabled) {
    background: rgba(4, 120, 115, 0.16);
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.ls-docs-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ls-doc-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 18px;

    &--red {
      background: #fee2e2;
      color: #dc2626;
    }
    &--orange {
      background: #fff4ed;
      color: #ea580c;
    }
    &--blue {
      background: #dbeafe;
      color: #2563eb;
    }
    &--green {
      background: #ecfdf5;
      color: #16a34a;
    }
    &--gray {
      background: #f3f4f6;
      color: #6b7280;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Small inline envelope icon — emails this doc to the participant.
  // Sits flush with the document name so the action is associated
  // with the file itself, not the row-level Preview/Download actions.
  &__mail {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: rgba(4, 120, 115, 0.1);
      color: var(--color-primary, #047873);
      outline: none;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    &--sending {
      color: var(--color-primary, #047873);
    }
  }

  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 2px;
  }

  &__type {
    background: #f3f4f6;
    padding: 2px 8px;
    border-radius: 6px;
    font-weight: 700;
    color: #374151;
  }

  &__sep {
    color: #d1d5db;
  }

  &__date {
    font-size: 11px;
    color: var(--color-primary, #047873);
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.ls-doc-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-family: 'Poppins', sans-serif;
  transition: opacity 0.15s, border-color 0.15s;

  i {
    font-size: 11px;
  }

  &--primary {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }
  }

  &--secondary {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e5e7eb;

    &:hover {
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
    }
  }
}

// ── Poll panel ────────────────────────────────────────────────────────
.ls-poll-header {
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    background: #16a34a;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    border-radius: 9999px;
  }

  &__sub {
    font-size: 12px;
    color: #6b7280;
    margin: 6px 0 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.ls-poll-body {
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ls-poll-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;

  &__type {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 9999px;
    background: #e6f7f5;
    color: var(--color-primary, #047873);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 12px;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__html {
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px;
    line-height: 1.5;

    ::v-deep p {
      margin: 0 0 6px;
    }
    ::v-deep p:last-child {
      margin-bottom: 0;
    }
    ::v-deep a {
      color: var(--color-primary, #047873);
      text-decoration: underline;
    }
    ::v-deep .button {
      display: inline-block;
      padding: 6px 14px;
      background: var(--color-primary, #047873);
      color: #ffffff;
      border-radius: 6px;
      text-decoration: none;
      font-weight: 600;
      font-size: 12px;
      margin-top: 6px;
    }
  }

  &__sub {
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: background 0.15s;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }
  }
}

// ── Themed overrides for the rendered Poll / Quiz / SinglePoll components.
// These deep selectors only apply inside the LiveStream Poll tab so the
// shared poll components keep their default look elsewhere.
.ls-poll-body {
  ::v-deep {
    // Outer wrappers — drop the loose tailwind padding/widths so the poll
    // sits flush inside our card panel
    .p-4 {
      padding: 0 !important;
    }
    .w-full.lg\:w-3\/5,
    .lg\:w-3\/5,
    .xl\:w-full {
      width: 100% !important;
    }

    // Question heading
    h1,
    h3 {
      font-family: 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      margin: 0 0 10px;
      line-height: 1.4;
    }

    // Description / html content
    .html-content,
    .text-sm {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      color: #6b7280;
      line-height: 1.5;
      margin-bottom: 12px;
    }

    // Free text input
    textarea {
      width: 100%;
      min-height: 96px;
      padding: 10px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      color: #111827;
      resize: vertical;
      transition: border-color 0.15s, box-shadow 0.15s;

      &::placeholder {
        color: #6b7280;
      }
      &:focus {
        outline: none;
        border-color: var(--color-primary, #047873);
        box-shadow: 0 0 0 3px rgba(13, 122, 110, 0.12);
      }
    }

    // Multiple-choice / quiz options. The shared component renders each
    // alternative as a div with the `.button` class — re-skin it as a
    // selectable card.
    .button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 14px;
      margin: 8px 0 0 !important;
      border: 1.5px solid #e5e7eb !important;
      border-radius: 10px;
      background: #ffffff !important;
      color: #111827 !important;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s, color 0.15s;

      &:hover {
        border-color: var(--color-primary, #047873) !important;
        background: #f0fbf9 !important;
      }

      // Selected state — the shared component toggles `bg-primary-bg`
      &.bg-primary-bg,
      &.font-bold {
        border-color: var(--color-primary, #047873) !important;
        background: var(--color-primary, #047873) !important;
        color: #ffffff !important;
      }

      span {
        font-weight: inherit;
      }
    }

    // Action row at the bottom (Close / Previous / Next / Send in)
    .flex.justify-between {
      gap: 8px;
      margin-top: 16px;
      flex-wrap: wrap;
    }

    .button-primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 9px 18px;
      border-radius: 10px;
      background: var(--color-primary, #047873) !important;
      color: #ffffff !important;
      border: none !important;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      width: auto;
      margin: 0 !important;

      &:hover {
        background: var(--color-primary-hover, #036663) !important;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .button-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 9px 16px;
      border-radius: 8px;
      background: #ffffff !important;
      color: #374151 !important;
      border: 1.5px solid #e5e7eb !important;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      width: auto;
      margin: 0 !important;

      &:hover {
        border-color: var(--color-primary, #047873) !important;
        color: var(--color-primary, #047873) !important;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.ls-panel__inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

// ── People panel ──────────────────────────────────────────────────────
.ls-people-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    font-weight: 500;
    margin: 4px 0 12px;
  }
}

.ls-people-search {
  position: relative;

  &__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 12px;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    padding: 8px 12px 8px 32px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    outline: none;
    font-family: 'Poppins', sans-serif;
    transition: border-color 0.15s;

    &::placeholder {
      color: #6b7280;
    }
    &:focus {
      border-color: var(--color-primary, #047873);
    }
  }
}

.ls-people-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ls-person-card {
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;

  &:hover,
  &--expanded {
    background: #f9fafb;
    border-color: #e5e7eb;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__avatar-wrap {
    position: relative;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  &__avatar {
    width: 40px !important;
    height: 40px !important;
    border-radius: 50%;
    overflow: hidden;
    background: #e5e7eb;

    ::v-deep img,
    ::v-deep .avatar {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  &__status-dot {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #16a34a;
    border: 2px solid #ffffff;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__role {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Right-side action group — DM icon + "View details" toggle.
  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__msg-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #6b7280;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
    transition: all 0.15s;

    &:hover {
      background: var(--color-primary, #047873);
      color: #ffffff;
      border-color: var(--color-primary, #047873);
    }
  }

  // Expanded contact details — revealed by "View details".
  &__expanded {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #eef0f2;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

// "View details" toggle pill (matches the lobby participant list).
.ls-moreinfo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 9999px;
  border: 1px solid #cfeae7;
  background: #f2faf9;
  color: var(--color-primary, #047873);
  font-size: 11.5px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &__chevron {
    font-size: 9px;
    transition: transform 0.2s ease;
  }

  &:hover,
  &--open {
    background: #e6f5f4;
    border-color: var(--color-primary, #047873);
  }
  &--open .ls-moreinfo__chevron {
    transform: rotate(180deg);
  }
}

// Expanded detail row — icon chip + truncated value.
.ls-detail {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #374151;
  font-size: 12.5px;

  &__icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--green {
      background: #e7f7ec;
      color: #16a34a;
    }
  }

  &__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover .ls-detail__text {
    color: var(--color-primary, #047873);
    text-decoration: underline;
  }
}

// Dark mode for the participant "View details" + expanded rows.
body[data-theme='dark'] {
  .ls-moreinfo {
    background: var(--t-primary-soft);
    border-color: var(--t-border-strong);
    color: var(--t-primary);

    &:hover,
    &--open {
      background: var(--t-primary-soft);
      border-color: var(--t-primary);
    }
  }
  .ls-person-card__expanded {
    border-top-color: var(--t-border-soft);
  }
  .ls-detail {
    color: var(--t-text-secondary);

    &__icon--teal {
      background: var(--t-primary-soft);
      color: var(--t-primary);
    }
    &__icon--green {
      background: var(--t-success-soft);
      color: var(--t-success);
    }
  }
}

.ls-empty {
  text-align: center;
  color: #6b7280;
  font-size: 13px;
  padding: 40px 16px;
}

// ── Shared header for Speakers / Sponsors / Sessions / Information
.ls-side-header,
.ls-info-header,
.ls-sessions-header {
  padding: 16px 18px 12px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
  flex-shrink: 0;

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__sub {
    font-size: 12px;
    color: var(--color-primary, #047873);
    margin: 4px 0 0;
    font-weight: 500;
  }
}

// ── Information panel ─────────────────────────────────────────────
.ls-info-body {
  padding: 16px 18px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  font-family: 'Poppins', sans-serif;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.ls-info {
  &__name {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 10px;
    line-height: 1.3;
  }

  &__when,
  &__where {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #374151;
    margin: 0 0 8px;

    i {
      color: var(--color-primary, #047873);
      font-size: 13px;
      width: 16px;
    }
  }

  &__about {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #f3f4f6;
    font-size: 13px;
    color: #374151;
    line-height: 1.6;

    ::v-deep p {
      margin: 0 0 8px;
    }
    ::v-deep p:last-child {
      margin-bottom: 0;
    }
  }
}

// ── Sessions panel ────────────────────────────────────────────────
.ls-sessions-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
  margin: 0;
}

.ls-session-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    box-shadow: 0 2px 6px rgba(4, 120, 115, 0.08);
  }

  &--current {
    border-color: var(--color-primary, #047873);
    background: #e6f5f4;
  }

  &__time {
    flex-shrink: 0;
    width: 64px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  &__date {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-primary, #047873);
    background: #e6f5f4;
    padding: 2px 6px;
    border-radius: 6px;
    line-height: 1.1;
    margin-bottom: 2px;
  }

  &__time-from {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    line-height: 1.1;
  }

  &__time-to {
    font-size: 11px;
    color: #6b7280;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    margin-bottom: 2px;
  }

  &__subtitle {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.3;
    margin-bottom: 4px;
  }

  &__speakers {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.3;

    i {
      color: var(--color-primary, #047873);
      font-size: 10px;
    }
  }

  &__live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #16a34a;
    margin-top: 6px;
    flex-shrink: 0;
    animation: ls-pulse 1.6s ease-in-out infinite;
  }
}

// ── Speakers panel ────────────────────────────────────────────────
.ls-speakers-list,
.ls-sponsors-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 12px;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ls-speaker-row,
.ls-sponsor-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  background: #ffffff;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f9fafb;
    border-color: #e5e7eb;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    color: #d1d5db;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.ls-speaker-row {
  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }
}

// ── Side-panel category filter (Speakers / Sponsors) ────────────
.ls-side-filter {
  position: relative;
  margin-top: 10px;

  &__select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 100%;
    padding: 8px 30px 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    background: #ffffff;
    font-size: 12px;
    font-weight: 500;
    color: #111827;
    cursor: pointer;
    outline: none;
    font-family: inherit;
    transition: border-color 0.15s;

    &:hover {
      border-color: #d1d5db;
    }
    &:focus {
      border-color: var(--color-primary, #047873);
    }
  }

  &__chevron {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    font-size: 10px;
  }
}

// ── Side-panel free-text search (Sessions / Speakers / Sponsors) ──
.ls-side-search {
  position: relative;
  margin-top: 10px;

  &__icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 9px 14px 9px 36px;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    background: #ffffff;
    font-size: 13px;
    color: #111827;
    outline: none;
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #9ca3af;
    }
    &:focus {
      border-color: var(--color-primary, #047873);
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.15);
    }
  }
}

// ── FAQ list inside Information panel ────────────────────────────
.ls-info__faqs {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ls-faq-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.15s;

  &--open {
    border-color: var(--color-primary, #047873);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    text-align: left;

    &:hover {
      background: #f9fafb;
    }

    i {
      color: var(--color-primary, #047873);
      font-size: 12px;
    }
  }

  &__title {
    flex: 1;
    min-width: 0;
    line-height: 1.3;
  }

  &__body {
    padding: 0 14px 14px;
    font-size: 13px;
    color: #374151;
    line-height: 1.6;

    ::v-deep p {
      margin: 0 0 8px;
    }
    ::v-deep p:last-child {
      margin-bottom: 0;
    }
  }
}

// ── Sponsors panel ────────────────────────────────────────────────
.ls-sponsor-row {
  &__logo-wrap {
    width: 56px;
    height: 40px;
    border-radius: 8px;
    background: #f9fafb;
    border: 1px solid #f1f2f4;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__logo-fallback {
    color: #9ca3af;
    font-size: 16px;
  }
}

@keyframes ls-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.85);
  }
}
</style>

<!-- Unscoped: target the vue-js-modal root container for our PrivateChatModal -->
<style>
.pcm-modal.v--modal,
.pcm-modal .v--modal {
  border-radius: 16px !important;
  overflow: hidden !important;
  padding: 0 !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25) !important;
}
</style>
